/* ============================================================================
 * Icon Cart Drawer — custom element + AJAX Cart API integration
 * v54 — Color de precios: las LÍNEAS del carrito (lo que se lleva el cliente)
 *       muestran el precio (descontado o no) en NEGRO. Solo las tarjetas de
 *       recomendados van en ROJO. El tachado (compare_at) se mantiene en gris.
 * v53 — Cuando el descuento al pedido está activo, CADA LÍNEA del carrito muestra
 *       su precio ya descontado (rojo) con la MISMA fórmula de reparto por línea
 *       que Shopify (precio − round(precio × % / 100)). Así carrusel = línea =
 *       total = checkout, al céntimo. Suma de líneas = total exacto.
 * v52 — El descuento al pedido se activa SOLO cuando hay al menos un producto
 *       recomendado (target) en el carrito (no solo el disparador). Las tarjetas
 *       del carrusel y el Quick Buy muestran el precio ya descontado (incentivo).
 *       Añadido descuento+código para los recomendados GENÉRICOS (fallback).
 *       Todo blindado por "código puesto" para que carrito = checkout siempre.
 * v51 — El descuento del bundle se aplica al PEDIDO COMPLETO (no por producto):
 *       el total del carrito muestra subtotal × (1 − %) y coincide exactamente
 *       con el código de descuento al pedido que se aplica en checkout. Las
 *       líneas y los recomendados muestran su precio normal. El bundle activo se
 *       calcula en _computeActiveBundle() ANTES de pintar el total.
 * v50 — Código de descuento por regla de bundle: cada regla (1–10) puede llevar
 *       un código de Shopify (campo `bundle-code`). Cuando la regla está activa,
 *       el botón "TRAMITAR PEDIDO" aplica ese código vía /discount/CODE?redirect
 *       =/checkout para que el descuento se aplique de verdad en el checkout.
 * v49 — FIX crítico: las reglas de bundle con sufijo numérico (2–10) se leían
 *       vía this.dataset['bundleHandlesN'], pero el navegador mapea
 *       data-bundle-handles-N → clave `bundleHandles-N` (guion conservado ante
 *       dígito), devolviendo undefined. Ahora se leen con getAttribute sobre el
 *       nombre de atributo crudo. Arregla colección 2–5 (rotas) y producto 6–10.
 * v48 — bundles por PRODUCTO concreto: además de las 5 reglas por colección
 *       (1–5) ahora hay 5 reglas por producto (6–10) cuyo disparador es un
 *       handle de producto directo. Se evalúan con prioridad sobre las de
 *       colección (más específicas). Mismo mecanismo de match/descuento.
 * v47 — bundle dynamic revalidation: si el cliente quita el producto que
 *       disparó un bundle, los items con _bundle_promo + _bundle_rule
 *       comprueban si queda algún OTRO trigger en el cart; si no →
 *       strip _bundle_promo/_bundle_rule via /cart/change.js → vuelven al
 *       precio normal automáticamente. Funciona también con items legacy
 *       sin _bundle_rule (lookup vía target→rule map).
 * --------------------------------------------------------------------------
 * Réplica pixel-perfect estilo Icon Amsterdam, conectado al carrito real de
 * Shopify mediante /cart.js, /cart/add.js, /cart/change.js y
 * /recommendations/products.json.
 *
 * Activadores externos:
 *   - Cualquier elemento con [data-icd-open] abre el drawer al hacer click.
 *   - Auto-apertura tras /cart/add.js (excepto en /cart).
 *   - ESC cierra. Click en overlay cierra. Click en [data-icd-close] cierra.
 * ============================================================================ */

(function () {
  'use strict';

  if (window.__iconCartDrawerLoaded) return;
  window.__iconCartDrawerLoaded = true;

  /* ── Helpers ─────────────────────────────────────────────────────────── */

  const escapeHTML = (str) =>
    String(str == null ? '' : str).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[c]));

  const sizedImage = (url, width) => {
    if (!url || typeof url !== 'string') return url || '';
    return url.replace(/(\.(?:jpg|jpeg|png|webp|gif))(\?.*)?$/i, `_${width}x$1$2`);
  };

  /**
   * srcset 1x/2x/3x para una caja de `base` px.
   *
   * `base` debe ser el LADO MAYOR de la caja CSS, no el ancho. Con
   * object-fit:cover y originales cuadrados (1080x1080), la imagen se escala
   * hasta cubrir el lado mayor y se recorta por el otro: pedir solo el ancho
   * dejaría la foto corta y borrosa en cajas verticales.
   *
   * Así cada pantalla baja EXACTAMENTE lo que necesita: menos peso en
   * pantallas normales y nitidez real en retina y 3x.
   */
  const srcsetFor = (url, base) =>
    url
      ? `${sizedImage(url, base)} 1x, ${sizedImage(url, base * 2)} 2x, ${sizedImage(url, base * 3)} 3x`
      : '';

  const isCartRoute = (url) =>
    typeof url === 'string' && /\/cart\/(add|change|update|clear)(\.|\?|$|\b)/i.test(url);

  const isCartAdd = (url) =>
    typeof url === 'string' && /\/cart\/add(\.|\?|$|\b)/i.test(url);

  // Escapa HTML primero y luego convierte **texto** en <strong>texto</strong>.
  // Permite que el admin use **palabras** dentro de los textos para negrita,
  // sin abrir XSS porque el resto de tags quedan escapados.
  const mdBold = (str) =>
    escapeHTML(String(str == null ? '' : str)).replace(
      /\*\*(.+?)\*\*/g,
      '<strong>$1</strong>'
    );

  /* ── The element ─────────────────────────────────────────────────────── */

  class IconCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.cart = null;
      this.isOpen = false;
      this.timerInterval = null;
      this.timerEndsAt = null;
      this.upsellsLoadedFor = null;
      this._savedScrollY = 0;
      this._pendingRender = null;
      // Contador de mutaciones de carrito PROPIAS en curso. Mientras sea >0, el
      // interceptor de fetch NO relanza un _fetchCart extra (el elemento ya se
      // re-renderiza solo) → evita el doble render/parpadeo tras cada acción.
      this._internalFetchActive = 0;
      // Firma del último upsell renderizado, para no reconstruir su HTML si no
      // ha cambiado (evita parpadeo de imágenes y reset de estado al re-render).
      this._upsellRenderedSig = null;
    }

    connectedCallback() {
      this._pageLoadAt = Date.now();
      // Detectar modo "page": el elemento es la página /cart, no un drawer flotante.
      // En modo page: siempre visible, sin overlay, sin scroll lock, sin auto-open.
      this._isPage = this.dataset.icdMode === 'page';
      this._readConfig();
      this._cacheNodes();
      this._bindEvents();
      this._interceptCart();
      if (this._isPage) {
        this.setAttribute('data-icd-open', '');
        this.setAttribute('aria-hidden', 'false');
        this.isOpen = true;
      }
      // Initial cart fetch
      this._fetchCart().catch(() => {});
    }

    /* ── Config & cache ──────────────────────────────────────────────── */

    _readConfig() {
      const d = this.dataset;

      // Productos por defecto del upsell — siempre se muestran (3 handles separados por comas).
      const upsellRaw = d.upsellDefault || '';
      const upsellDefault = String(upsellRaw)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      // Bundles — hasta 10 reglas. Posiciones 0–4 (reglas 1–5) son por
      // COLECCIÓN: si el carrito tiene un producto de esa colección, mostramos
      // esos targets. Posiciones 5–9 (reglas 6–10) son por PRODUCTO concreto:
      // el disparador es directamente el/los handle(s) de producto tecleados.
      // El mecanismo de match es idéntico (handles → Set), solo cambia de
      // dónde viene la lista de handles. Para una regla por producto el Liquid
      // pasa el handle del producto tal cual en data-bundle-handles-N.
      // La selección se evalúa con prioridad a las reglas por producto (ver
      // _renderUpsell): una regla de producto concreto es más específica que
      // una de colección. La primera que matchea gana; si ninguna, default.
      const parseList = (raw) =>
        String(raw || '').split(',').map((s) => s.trim()).filter(Boolean);

      // IMPORTANTE: NO se puede leer estos valores vía `this.dataset` porque el
      // navegador, al convertir `data-bundle-handles-6` a clave de dataset,
      // conserva el guion antes del dígito → la clave real es `bundleHandles-6`
      // (NO `bundleHandles6`). Eso hacía que las reglas 2–5 (y las nuevas 6–10)
      // devolvieran undefined y nunca matchearan. Leemos el atributo crudo con
      // getAttribute, cuyo nombre es inequívoco.
      const attr = (base, suffix) =>
        this.getAttribute('data-' + base + (suffix ? '-' + suffix : '')) || '';
      const readBundle = (suffix) => ({
        handles: new Set(parseList(attr('bundle-handles', suffix))),
        targets: parseList(attr('bundle-targets', suffix)),
        discount: parseInt(attr('bundle-discount', suffix) || '0', 10) || 0,
        // Código de descuento de Shopify a aplicar en checkout cuando esta
        // regla esté activa. Vacío = no se aplica ningún código.
        code: (attr('bundle-code', suffix) || '').trim(),
      });

      const bundles = [
        readBundle(''),    // regla 1 — colección (sin sufijo, retrocompatible)
        readBundle('2'),   // regla 2 — colección
        readBundle('3'),   // regla 3 — colección
        readBundle('4'),   // regla 4 — colección
        readBundle('5'),   // regla 5 — colección
        readBundle('6'),   // regla 6 — producto concreto
        readBundle('7'),   // regla 7 — producto concreto
        readBundle('8'),   // regla 8 — producto concreto
        readBundle('9'),   // regla 9 — producto concreto
        readBundle('10'),  // regla 10 — producto concreto
      ];

      this.config = {
        threshold: (function () {
          // 0 = envío SIEMPRE gratis (sin mínimo). No usar `|| 15000` porque 0
          // es "falsy" y caería al valor por defecto por error.
          var _t = parseInt(d.threshold, 10);
          return isNaN(_t) ? 15000 : _t;
        })(),
        currency: d.currency || 'EUR',
        locale: d.locale || 'es-ES',
        template: d.template || '',
        showTimer: d.showTimer === 'true',
        countdown: parseInt(d.countdown, 10) || 90,
        timerText: d.timerText || 'TU PEDIDO ESTÁ RESERVADO DURANTE',
        showUpsell: d.showUpsell === 'true',
        upsellTitle: d.upsellTitle || 'La gente también compró',
        upsellButtonLabel: d.upsellButtonLabel || '+ AÑADIR',
        savingsShow: d.savingsShow === 'true',
        savingsText: d.savingsText || 'AHORRA',
        upsellDefault: upsellDefault,
        // Descuento + código para los recomendados GENÉRICOS (fallback): se usan
        // cuando NINGUNA regla de bundle matchea. Funcionan igual que una regla:
        // el descuento al pedido se activa cuando el cliente añade uno de esos
        // productos por defecto, y se aplica vía el código en checkout.
        upsellDefaultDiscount: parseInt(this.getAttribute('data-upsell-default-discount') || '0', 10) || 0,
        upsellDefaultCode: (this.getAttribute('data-upsell-default-code') || '').trim(),
        bundles: bundles,
        emptyLabel: d.emptyLabel || 'Tu cesta está vacía',
        removeLabel: d.removeLabel || 'Eliminar',
        shippingTextProgress: d.shippingTextProgress || '¡Gasta {amount} más y consigue envío gratis!',
        shippingTextReached: d.shippingTextReached || '¡Felicidades! Has conseguido **envío gratis**.',
      };
    }

    _cacheNodes() {
      this.$panel = this.querySelector('.icd-panel');
      this.$body = this.querySelector('[data-icd-body]');
      this.$lineItems = this.querySelector('[data-icd-line-items]');
      this.$empty = this.querySelector('[data-icd-empty]');
      this.$timer = this.querySelector('[data-icd-timer]');
      this.$timerClock = this.querySelector('[data-icd-timer-clock]');
      this.$shippingWrap = this.querySelector('[data-icd-shipping]');
      this.$shippingText = this.querySelector('[data-icd-shipping-text]');
      this.$progress = this.querySelector('[data-icd-progress]');
      this.$progressFill = this.querySelector('[data-icd-progress-fill]');
      this.$totalFinal = this.querySelector('[data-icd-total-final]');
      this.$totalOriginal = this.querySelector('[data-icd-total-original]');
      this.$savings = this.querySelector('[data-icd-savings]');
      this.$count = this.querySelector('[data-icd-count]');
      this.$upsell = this.querySelector('[data-icd-upsell]');
      this.$upsellCarousel = this.querySelector('[data-icd-upsell-carousel]');
      this.$upsellNav = this.querySelector('[data-icd-upsell-nav]');
      this.$upsellPrev = this.querySelector('[data-icd-upsell-prev]');
      this.$upsellNext = this.querySelector('[data-icd-upsell-next]');
      // Quick Buy elements live OUTSIDE this element (siblings)
      this.$qb = document.querySelector('[data-icd-qb]');
      this.$qbBody = document.querySelector('[data-icd-qb-body]');
    }

    /* ── Money formatting ────────────────────────────────────────────── */

    _formatMoney(cents) {
      const amount = (Number(cents) || 0) / 100;
      const code = (this.cart && this.cart.currency) || this.config.currency || 'EUR';
      // Símbolo SIEMPRE antes del número: €54,95 (no 54,95 €)
      const symbols = { EUR: '€', USD: '$', GBP: '£', MXN: '$', BRL: 'R$' };
      // Separador decimal SEGÚN la moneda: punto en USD/MXN ($44.95, $899.00),
      // coma en EUR (44,95 €) → cada mercado se ve natural, no "$44,95".
      const locales = { EUR: 'es-ES', USD: 'en-US', GBP: 'en-GB', MXN: 'es-MX', BRL: 'pt-BR' };
      const sym = symbols[code] || code + ' ';
      let formatted;
      try {
        formatted = new Intl.NumberFormat(locales[code] || 'en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch (e) {
        formatted = amount.toFixed(2);
      }
      return sym + formatted;
    }

    /* ── Open / close ────────────────────────────────────────────────── */

    open() {
      if (this._isPage) return;        // En modo page no se abre/cierra
      if (this.isOpen) return;
      this.isOpen = true;
      this.setAttribute('data-icd-open', '');
      this.setAttribute('aria-hidden', 'false');
      if (this.$panel) this.$panel.setAttribute('aria-hidden', 'false');

      this._lockScroll();
      this._startTimer();
      this._loadUpsells();
      this.dispatchEvent(new CustomEvent('icd:opened', { bubbles: true }));
    }

    close() {
      if (this._isPage) return;        // En modo page no se cierra
      if (!this.isOpen) return;
      this.isOpen = false;
      this.removeAttribute('data-icd-open');
      this.setAttribute('aria-hidden', 'true');
      if (this.$panel) this.$panel.setAttribute('aria-hidden', 'true');

      this._unlockScroll();
      this._stopTimer();
      this.dispatchEvent(new CustomEvent('icd:closed', { bubbles: true }));
    }

    toggle() {
      this.isOpen ? this.close() : this.open();
    }

    _lockScroll() {
      if (this._isPage) return;        // Sin scroll lock en modo page
      this._savedScrollY = window.scrollY || window.pageYOffset || 0;
      document.documentElement.classList.add('icd-locked');
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this._savedScrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    }

    _unlockScroll() {
      if (this._isPage) return;        // Sin scroll lock en modo page
      document.documentElement.classList.remove('icd-locked');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, this._savedScrollY || 0);
    }

    /* ── Reservation timer ───────────────────────────────────────────── */

    _startTimer() {
      if (!this.config.showTimer || !this.$timer) return;
      this.$timer.hidden = false;

      if (!this.timerEndsAt || this.timerEndsAt <= Date.now()) {
        this.timerEndsAt = Date.now() + this.config.countdown * 1000;
      }

      const tick = () => {
        const remaining = Math.max(0, Math.floor((this.timerEndsAt - Date.now()) / 1000));
        const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
        const ss = String(remaining % 60).padStart(2, '0');
        if (this.$timerClock) this.$timerClock.textContent = `${mm}:${ss}`;
        if (remaining <= 0) {
          // Al llegar a 00:00 paramos y ocultamos el banner para no quedar "pillado"
          this._stopTimer();
          if (this.$timer) this.$timer.hidden = true;
        }
      };

      tick();
      if (this.timerInterval) clearInterval(this.timerInterval);
      this.timerInterval = setInterval(tick, 1000);
    }

    _stopTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    }

    /* ── Cart fetching & rendering ───────────────────────────────────── */

    async _fetchCart({ autoOpen = false, skipRevalidate = false } = {}) {
      const seq = (this._fetchSeq = (this._fetchSeq || 0) + 1);
      try {
        const oldCount = this.cart ? Number(this.cart.item_count) || 0 : -1;
        const res = await fetch('/cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        const data = await res.json();
        // Si mientras esperábamos arrancó un _fetchCart más nuevo, descartamos
        // esta respuesta (podría ser más vieja) para no pisar el carrito con datos
        // caducados en redes lentas.
        if (seq !== this._fetchSeq) return;
        this.cart = data;
        // Pre-cargar datos de producto en paralelo para tener compare_at_price
        // disponible en el render (fallback cuando la variante no lo tiene).
        await this._fetchMissingProducts();
        // Revalidación bundle: si alguna línea tiene _bundle_promo pero el
        // producto que lo disparó (rule trigger) ya NO está en el carrito,
        // le quitamos la promo y vuelve a precio normal automáticamente.
        if (!skipRevalidate) {
          await this._revalidateBundleDiscounts();
        }
        this._scheduleRender();

        if (autoOpen) {
          // 3 condiciones para autoabrir y evitar que apps de terceros
          // (ej. Shipping Protection) abran el carrito al cargar la página:
          //   1. No estamos en /cart
          //   2. El número de items HA AUMENTADO (no es un refresh o un change)
          //   3. Han pasado más de 2.5s desde que cargó la página (descarta
          //      adds automáticos de apps que disparan a los milisegundos).
          const path = window.location.pathname || '';
          const onCartPage = /\/cart\/?$/.test(path);
          const newCount = Number(this.cart.item_count) || 0;
          const countIncreased = oldCount >= 0 && newCount > oldCount;
          const timeSinceLoad = Date.now() - (this._pageLoadAt || 0);
          const pastGrace = timeSinceLoad > 2500;

          // Add iniciado por el USUARIO: si hubo un clic/tecla REAL en un botón
          // de añadir en los últimos 5s, abrimos SIEMPRE (sin esperar el margen
          // de 2,5s y sin depender del conteo en frío). Como la apertura solo se
          // alcanza tras un /cart/add con respuesta OK (HTTP 2xx), el producto ya
          // está dentro: no se abre en errores ni en refrescos. El margen de
          // 2,5s + conteo queda SOLO para adds automáticos de apps (sin clic).
          const userInitiated = (Date.now() - (this._lastUserAddClick || 0)) < 5000;
          const allow = userInitiated ? true : (countIncreased && pastGrace);

          if (!onCartPage && newCount > 0 && allow) {
            this.open();
          }
        }
      } catch (e) {
        console.warn('[icd] _fetchCart failed', e);
      }
    }

    _scheduleRender() {
      if (this._pendingRender) return;
      this._pendingRender = requestAnimationFrame(() => {
        this._pendingRender = null;
        this._render();
      });
    }

    _render() {
      if (!this.cart) return;

      const count = Number(this.cart.item_count) || 0;
      if (this.$count) this.$count.textContent = String(count);

      const isEmpty = count === 0;
      if (this.$lineItems) this.$lineItems.hidden = isEmpty;
      if (this.$empty) this.$empty.hidden = !isEmpty;
      // Marca el root con icd-is-empty: oculta header/timer/envío/footer
      // y centra el bloque empty.
      this.classList.toggle('icd-is-empty', isEmpty);

      // Calcular qué regla de bundle está activa ANTES de pintar el total,
      // porque el total aplica el descuento del bundle al PEDIDO COMPLETO.
      this._computeActiveBundle();

      this._renderLineItems();
      this._renderTotals();
      this._renderShipping();
      this._loadUpsells();
      this._syncHeaderCount(count);
    }

    _syncHeaderCount(count) {
      const hasItems = count > 0;
      // Update theme's existing cart bubble (cart-bubble snippet)
      document.querySelectorAll('.cart-bubble__text-count').forEach((el) => {
        el.textContent = hasItems ? String(count) : '';
        // Liquid añade la clase 'hidden' al número cuando la página se renderiza
        // con el carrito vacío. Hay que quitarla al añadir (si no, el número no
        // vuelve a salir sin recargar) y volver a ponerla al vaciar.
        el.classList.toggle('hidden', !hasItems);
      });
      // El círculo (badge) nace con 'visually-hidden' si la página cargó con el
      // carrito vacío. Lo quitamos al añadir y lo ponemos al vaciar; así el
      // círculo aparece/desaparece correctamente sin necesidad de recargar.
      document.querySelectorAll('.cart-bubble').forEach((el) => {
        el.classList.toggle('visually-hidden', !hasItems);
      });
      document.querySelectorAll('cart-icon').forEach((el) => {
        el.classList.toggle('header-actions__cart-icon--has-cart', hasItems);
      });
    }

    _renderLineItems() {
      if (!this.$lineItems) return;
      if (!this.cart || !this.cart.items || this.cart.items.length === 0) {
        this.$lineItems.innerHTML = '';
        return;
      }
      // Pasamos `index + 1` (1-based) para usarlo como `line` en /cart/change.js.
      // El `line` index es MÁS FIABLE que `id: key` cuando hay propiedades:
      // Shopify a veces interpreta `id` como variant_id y descarta el hash de
      // properties, lo que duplica líneas en lugar de cambiarles la cantidad.
      // ORDEN DE VISUALIZACION: productos principales arriba, REGALOS (_gift) abajo.
      // Se conserva el indice ORIGINAL de linea (idx+1) para /cart/change.js: solo cambia
      // la POSICION visual, nunca el line index real -> borrar/cantidad nunca falla.
      // El sort de JS es estable -> los productos normales mantienen su orden entre si.
      const isGift = (it) => !!(it && it.properties && (it.properties._gift || it.properties['_gift']));
      // ORDEN: Shopify devuelve los items MÁS NUEVOS primero. Queremos ORDEN DE
      // INSERCIÓN (el primero añadido -producto de la ficha- arriba; cada nuevo
      // -recomendado del carrito- SIEMPRE debajo, nunca encima). Invertimos los
      // no-regalo para pasar de "nuevo primero" a "nuevo al final". Los REGALOS
      // (_gift) van al fondo. Cada item conserva su `line` real (idx+1) para
      // /cart/change.js → borrar/cantidad nunca falla.
      const rows = this.cart.items.map((item, idx) => ({ item, line: idx + 1 }));
      const nonGifts = rows.filter((r) => !isGift(r.item)).reverse();
      const gifts = rows.filter((r) => isGift(r.item));
      const html = [...nonGifts, ...gifts]
        .map(({ item, line }) => this._lineItemHTML(item, line))
        .join('');
      this.$lineItems.innerHTML = html;
    }

    _lineItemHTML(item, lineIndex) {
      const qty = Number(item.quantity) || 1;
      // Precio REAL por unidad: calculado desde final_line_price / qty para
      // reflejar descuentos automáticos por cantidad (ej: 10% en 2 uds,
      // 20% en 3, 30% en 4+). Cuando no hay descuento de volumen,
      // final_line_price === price × qty → result == price (sin cambios).
      const finalLinePriceNum =
        Number(item.final_line_price != null ? item.final_line_price
        : (item.line_price != null ? item.line_price
        : (Number(item.final_price != null ? item.final_price : item.price) || 0) * qty)) || 0;
      const unitPriceNum = qty > 0 ? Math.round(finalLinePriceNum / qty) : 0;
      // Cuando el descuento al PEDIDO está activo (hay un recomendado en el
      // carrito), cada línea muestra su precio YA descontado. Usamos EXACTAMENTE
      // la misma fórmula de reparto que Shopify aplica con el código en checkout:
      // descuento de la línea = round(precioLínea × % / 100); precio neto =
      // precioLínea − ese descuento. Así la suma de líneas = total = checkout,
      // al céntimo. Si no hay descuento activo → precio real de Shopify.
      const lineNet = this._lineNetPrice(item);
      const unitPrice = qty > 0 ? Math.round(lineNet / qty) : lineNet;
      // El compare_at puede venir directo del item, o haber que buscarlo en el
      // producto si esta variante en concreto no tiene. _getEffectiveCompareAt
      // hace ese fallback. Se mantiene IGUAL aunque cambie la cantidad
      // (representa el "precio marca/MSRP" sin promociones).
      const compareAtNum = this._getEffectiveCompareAt(item);
      let compareAt = compareAtNum > unitPriceNum ? compareAtNum : null;
      // Caja 98x98 (.icd-li-image) -> base 98
      // Miniatura PERSONALIZADA del cojín por pack (SOLO este producto, SOLO en el carrito):
      // cada variante 1/2/3 cojines muestra su propia imagen, distinta de las fotos de Shopify.
      const SOM_CART_IMG = {
        59221635432793: 'https://cdn.shopify.com/s/files/1/1064/7293/1673/files/66c750c1-c4e5-4bf1-b6b4-87e7c6d009f4.png?v=1789395485',
        59221635465561: 'https://cdn.shopify.com/s/files/1/1064/7293/1673/files/11ca0dd9-6342-4864-866d-29537478db95.png?v=1789395496',
        59221635498329: 'https://cdn.shopify.com/s/files/1/1064/7293/1673/files/99e4be4f-bf2e-4c25-8fd4-316318561772.png?v=1789395625',
      };
      const imgOverride = SOM_CART_IMG[item.variant_id];
      const imgBase = imgOverride || (item.featured_image && item.featured_image.url) || item.image || '';
      const imgUrl = sizedImage(imgBase, 98);
      const imgSrcset = srcsetFor(imgBase, 98);
      const variantTitle =
        item.variant_title && item.variant_title !== 'Default Title'
          ? item.variant_title
          : '';
      const key = item.key;
      const url = item.url || '#';
      // PACK (1/2/3 cojines = 3 variantes): el selector de cantidad muestra el Nº DE COJINES y
      // los botones +/- cambian de pack (variante), no suman unidades de la misma variante.
      // Cojin SUELTO (unidad extra por encima del pack maximo): variante "1 cojin" marcada con _unit.
      // Se comporta como una linea de cantidad NORMAL (no como pack), sin tope.
      const isUnit = !!(item.properties && (item.properties._unit || item.properties['_unit']));
      const packIdx = this._packTierIndex(item.variant_id);
      const isPack = packIdx >= 0 && !isUnit;
      const packT = isPack ? this._packTiers() : null;
      const displayQty = isPack ? (packT[packIdx].count || qty) : qty;
      const packPrev = isPack && packIdx > 0 ? packT[packIdx - 1].id : '';
      const packNext = isPack && packIdx < packT.length - 1 ? packT[packIdx + 1].id : '';
      // REGALO (funda gratis): se gestiona solo con el pack -> sin selector de cantidad ni papelera
      // (evita que el cliente lo edite y provoque saltos/re-añadidos). Solo muestra "Regalo".
      const isGiftLine = !!(item.properties && (item.properties._gift || item.properties['_gift']));
      // Producto GRATIS (regalo o precio 0): en el hueco de la variante mostramos "GRATIS".
      const isFree = isGiftLine || unitPriceNum === 0;
      // Si es gratis y no tiene compare_at, mostramos su VALOR normal tachado junto al €0
      // (igual que un producto rebajado: precio real 0 en navy + tachado en gris).
      if (isFree && !compareAt) {
        const origUnit = Number(item.original_price != null ? item.original_price : item.price) || 0;
        if (origUnit > unitPriceNum) compareAt = origUnit;
      }
      // % de AHORRO por línea (badge "Ahorra un X%"): solo con descuento real y NO en gratis.
      // Redondeado a múltiplos de 5 (20, 25, 30, 35, 50...), nunca cifras raras.
      const savingsPct = (!isFree && compareAt && compareAt > unitPriceNum)
        ? Math.round((((compareAt - unitPriceNum) / compareAt) * 100) / 5) * 5
        : 0;
      // Badge de línea: por defecto "Ahorra un X%". Para el pack de 2 cojines (variante 59221635465561)
      // mostramos "1+1 GRATIS" (coherente con la oferta de la ficha).
      let _saveText = savingsPct ? `Ahorra un ${savingsPct}%` : '';
      if (Number(item.variant_id) === 59221635465561) _saveText = '1+1 GRATIS';
      const saveBadge = _saveText ? `<span class="icd-li-save">${_saveText}</span>` : '';

      // Carga de la línea: PÍLDORA con TRES PUNTITOS centrada SOBRE LA IMAGEN
      // del producto, clon EXACTO de icon-amsterdam (.pill-loader): pastilla
      // blanca 32×16 totalmente redondeada, 3 puntos de 4px con separación 4px.
      // Aparece mientras dura la petición del carrito (cuando la línea tiene
      // aria-busy, que ya pone _setLineBusy) y se oculta al re-renderizar.
      // aria-hidden: es solo feedback visual; el estado real lo anuncia aria-busy.
      const pillLoader =
        '<span class="icd-pill-loader" aria-hidden="true"><span class="icd-loader-dots"><span></span><span></span><span></span></span></span>';

      return `
        <div class="icd-line-item${isGiftLine ? ' icd-line-item--gift' : ''}" data-icd-line data-icd-line-key="${escapeHTML(key)}" data-icd-line-index="${lineIndex}">
          <a class="icd-li-image-wrap" href="${escapeHTML(url)}" tabindex="-1">
            <img class="icd-li-image"
                 src="${escapeHTML(imgUrl)}"
                 srcset="${escapeHTML(imgSrcset)}"
                 alt="${escapeHTML(item.product_title || item.title || '')}"
                 width="98" height="98" loading="lazy" decoding="async">
            ${pillLoader}
          </a>
          <div class="icd-li-info">
            <div class="icd-li-namerow">
              <a class="icd-li-title" href="${escapeHTML(url)}">${escapeHTML(item.product_title || item.title || '')}</a>
              ${'' /* Papelera (borrar producto): clon de serenium — icono libre Lucide "trash"
                     (outline, MIT) a stroke-width 1.6 (= 22,4% de tinta, idéntico a su fa-trash
                     regular), gris #999, 14px, botón 20x20 transparente arriba a la derecha.
                     Usa el handler [data-icd-remove] que YA existía (quita la línea con qty 0). */}
              ${isGiftLine ? '' : `<button type="button" class="icd-li-remove"
                      data-icd-remove${isPack ? ' data-icd-pack="1"' : ''}
                      data-icd-line-key="${escapeHTML(key)}"
                      data-icd-line-index="${lineIndex}"
                      aria-label="Quitar ${escapeHTML(item.product_title || item.title || '')}">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
                  <path d="M3 6h18"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
              </button>`}
            </div>
            ${variantTitle ? `<p class="icd-li-variant">${escapeHTML(variantTitle)}${saveBadge ? ` ${saveBadge}` : ''}</p>` : (isFree ? `<p class="icd-li-variant">GRATIS</p>` : (saveBadge ? `<p class="icd-li-variant">${saveBadge}</p>` : ''))}
            <div class="icd-li-bottom">
              ${isGiftLine ? `<span class="icd-li-gift"><svg class="icd-li-gift-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"/><circle cx="7.5" cy="7.5" r="1.15" fill="currentColor" stroke="none"/></svg>Regalo incluido</span>` : ''}
              <div class="icd-qty">
              <button type="button" class="icd-qty-btn"
                      data-icd-qty-dec
                      data-icd-line-key="${escapeHTML(key)}"
                      data-icd-line-index="${lineIndex}"
                      data-icd-qty="${qty}"
                      ${isPack ? `data-icd-pack="1" data-icd-pack-prev="${packPrev}"` : ''}
                      aria-label="Reducir cantidad">
                <svg class="icd-qty-glyph" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path d="M2 6h8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
                </svg>
              </button>
              <input class="icd-qty-input" type="text" inputmode="numeric"
                     value="${displayQty}"
                     data-icd-qty-input
                     data-icd-line-key="${escapeHTML(key)}"
                     aria-label="Cantidad" readonly>
              <button type="button" class="icd-qty-btn"
                      data-icd-qty-inc
                      data-icd-line-key="${escapeHTML(key)}"
                      data-icd-line-index="${lineIndex}"
                      data-icd-qty="${qty}"
                      ${isPack ? `data-icd-pack="1" data-icd-pack-next="${packNext}"` : ''}
                      aria-label="Aumentar cantidad">
                <svg class="icd-qty-glyph" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path d="M2 6h8M6 2v8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
              <div class="icd-li-prices">
                <span class="icd-li-price-final${compareAt ? ' icd-li-price-final--sale' : ''}">${this._formatMoney(unitPrice)}</span>
                ${compareAt ? `<span class="icd-li-price-original">${this._formatMoney(compareAt)}</span>` : ''}
              </div>
            </div>
          </div>
        </div>`;
    }

    /* Extrae el % de descuento bundle de las properties de un line item.
       Las properties pueden venir como objeto (Shopify standard) o como
       array de pares (algunos contextos legacy). Returns 0 si no hay. */
    _getBundleDiscount(item) {
      if (!item || !item.properties) return 0;
      let val = null;
      if (Array.isArray(item.properties)) {
        const found = item.properties.find((p) =>
          p && (p.name === '_bundle_promo' || (Array.isArray(p) && p[0] === '_bundle_promo'))
        );
        if (found) val = found.value != null ? found.value : (Array.isArray(found) ? found[1] : null);
      } else if (typeof item.properties === 'object') {
        val = item.properties._bundle_promo;
      }
      if (!val) return 0;
      const m = String(val).match(/(\d+)/);
      const n = m ? parseInt(m[1], 10) : 0;
      return n > 0 && n < 100 ? n : 0;
    }

    /* % de descuento que recae sobre UNA línea concreta. El descuento del bundle
       se aplica SOLO a los productos recomendados (targets) que estén en el
       carrito; el disparador y el resto se quedan a precio completo. Esto coincide
       EXACTAMENTE con el código de Shopify "% en estos productos" del checkout. */
    _lineDiscountPct(item) {
      const pct = Number(this._targetDiscountPct) || 0;
      if (pct <= 0 || !item || !item.handle) return 0;
      const targets = this._activeBundleTargets || [];
      if (targets.indexOf(item.handle) === -1) return 0;
      // Si el código del bundle YA está aplicado a esta línea en la sesión (el
      // cliente ya pasó por el enlace de "Tramitar pedido" y el código quedó
      // pegado), final_line_price YA viene descontado por Shopify → NO volver a
      // descontar, o saldría el DOBLE. En ese caso mostramos el precio tal cual.
      if (this._lineHasBundleDiscount(item)) return 0;
      return pct;
    }

    /* ¿Esta línea ya trae aplicado el código de descuento del bundle (CARRITO-…)?
       Se mira en las line_level_discount_allocations que envía Shopify. Evita el
       doble descuento cuando el código se queda en la sesión tras ir a checkout. */
    _lineHasBundleDiscount(item) {
      const allocs = item && item.line_level_discount_allocations;
      if (!Array.isArray(allocs) || !allocs.length) return false;
      const code = (this._activeBundleCode || '').trim().toUpperCase();
      if (!code) return false;
      return allocs.some((a) => {
        const da = a && a.discount_application;
        const t = da && (da.title || da.code);
        return t && String(t).trim().toUpperCase() === code;
      });
    }

    /* Precio NETO de la línea EXACTAMENTE como lo cobra Shopify en caja.
       Shopify aplica el % POR UNIDAD redondeando hacia abajo (floor) y multiplica
       por la cantidad: descuento = floor(precioUnidad × % / 100) × cantidad.
       (Verificado en vivo: bolso 26,67 ×3 → −13,98 → 36,03, NO 36,00/36,01.)
       Si la línea YA trae el código aplicado en sesión, _lineDiscountPct devuelve
       0 y se muestra su final_line_price tal cual. Así carrito = caja al céntimo. */
    _lineNetPrice(item) {
      const qty = Number(item && item.quantity) || 1;
      const lineFull = Number(item.final_line_price != null ? item.final_line_price
        : (item.line_price != null ? item.line_price
        : (Number(item.final_price != null ? item.final_price : item.price) || 0) * qty)) || 0;
      const pct = this._lineDiscountPct(item);
      if (pct <= 0) return lineFull;
      const unit = qty > 0 ? lineFull / qty : lineFull;
      const unitDisc = Math.floor(unit * pct / 100);
      return lineFull - unitDisc * qty;
    }

    _renderTotals() {
      if (!this.cart) return;

      // Descuento de bundle aplicado al PEDIDO COMPLETO (coincide con el código
      // de descuento que se aplica en checkout). El subtotal es el total real
      // de Shopify (precios de oferta vigentes = lo que pagaría SIN bundle).
      const items = (this.cart && this.cart.items) || [];
      const subtotal = Number(this.cart.total_price) || 0;

      // El descuento del bundle recae SOLO sobre las líneas recomendadas (targets)
      // que estén en el carrito — ver _lineDiscountPct(). El disparador y el resto
      // se quedan a precio completo. La suma de líneas = total = checkout, al céntimo.
      // Si falta cualquier condición → 0 en esa línea → precio completo. Nunca miente.

      // FINAL = suma de cada línea YA descontada con la MISMA fórmula de reparto
      // por línea que usa Shopify con el código en checkout
      // (descuento_línea = round(precioLínea × % / 100)). Así el total del
      // carrito coincide al céntimo con la pantalla de pago Y con la suma de las
      // líneas que se muestran en el carrito. "original" tachado = MSRP/compare.
      // TACHADO (original) del TOTAL = suma por linea de max(compare_at de la variante, precio
      // ORIGINAL de linea). Asi:
      //  - Muestra SIEMPRE el precio de comparacion (gris) cuando la variante tiene compare_at
      //    (p.ej. pack 1.399 con compare 1.999 -> tachado 1.999), y el tachado del TOTAL = suma
      //    de los tachados de cada linea (coherente con las lineas).
      //  - Incluye el valor de lineas con descuento de linea/regalo via original_line_price
      //    (funda regalo 0 pero su original 399 cuenta en el "antes").
      //  - Si una linea no tiene compare ni descuento, aporta su precio normal (no infla nada) y
      //    si NINGUNA linea tiene ahorro, original == final -> el tachado NO se muestra.
      // _getEffectiveCompareAt usa la VARIANTE EXACTA (arreglado) -> nunca coge el compare del
      // pack mas caro (aquel bug daba 4.398). El importe que se PAGA (final) sigue = checkout.
      let final = 0;
      let original = 0;
      items.forEach((item) => {
        const qty = Number(item.quantity) || 1;
        final += this._lineNetPrice(item);
        const origLine = Number(item.original_line_price != null ? item.original_line_price
          : (item.line_price != null ? item.line_price
          : (Number(item.original_price != null ? item.original_price : item.price) || 0) * qty)) || 0;
        const unitOrig = qty > 0 ? Math.round(origLine / qty) : 0;
        const effectiveCompare = this._getEffectiveCompareAt(item);
        const marked = effectiveCompare > unitOrig ? effectiveCompare : unitOrig;
        original += marked * qty;
      });
      if (original < subtotal) original = subtotal;
      if (original < final) original = final;

      const savings = original > final ? original - final : 0;

      if (this.$totalFinal) this.$totalFinal.textContent = this._formatMoney(final);

      // Mostrar los 3 (tachado + real + badge) cuando hay ahorro real.
      // Si no hay ahorro (productos sin compare_at_price), se ocultan los
      // dos opcionales para no mostrar "-€0,00" o cajas vacías.
      if (savings > 0) {
        if (this.$totalOriginal) {
          this.$totalOriginal.textContent = this._formatMoney(original);
          this.$totalOriginal.hidden = false;
        }
        if (this.$savings) {
          this.$savings.textContent = `-${this._formatMoney(savings)}`;
          this.$savings.hidden = false;
        }
      } else {
        if (this.$totalOriginal) {
          this.$totalOriginal.hidden = true;
          this.$totalOriginal.textContent = '';
        }
        if (this.$savings) {
          this.$savings.hidden = true;
          this.$savings.textContent = '';
        }
      }
    }

    _renderShipping() {
      if (!this.cart || !this.$progressFill) return;
      const subtotal = Number(this.cart.total_price) || 0;
      const threshold = this.config.threshold;
      const remaining = Math.max(0, threshold - subtotal);
      const progress = threshold > 0 ? Math.min(1, subtotal / threshold) : 1;

      this.$progressFill.style.transform = `scaleX(${progress})`;
      if (this.$progress) {
        this.$progress.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
        // Alcanzado el umbral → barra verde 100% (cambia color vía CSS)
        if (remaining <= 0) {
          this.$progress.setAttribute('data-icd-reached', '');
        } else {
          this.$progress.removeAttribute('data-icd-reached');
        }
      }

      if (remaining <= 0) {
        // Soporta **negrita** en el texto vía mdBold
        if (this.$shippingText) {
          this.$shippingText.innerHTML = mdBold(this.config.shippingTextReached);
        }
      } else {
        const amount = this._formatMoney(remaining);
        const txt = this.config.shippingTextProgress.replace('{amount}', amount);
        if (this.$shippingText) {
          // Wrap the amount with bold span + soporta **negrita** en el resto del texto
          const safe = mdBold(txt).replace(
            escapeHTML(amount),
            `<span class="icd-shipping-amount">${escapeHTML(amount)}</span>`
          );
          this.$shippingText.innerHTML = safe;
        }
      }
    }

    /* ── Upsell ──────────────────────────────────────────────────────── */

    /* Actualiza el href del botón "TRAMITAR PEDIDO" para que aplique el código
       de descuento de la regla de bundle activa usando el enlace oficial de
       Shopify (/discount/CODE?redirect=/checkout), que aplica el código y
       redirige al checkout. Si no hay código activo, deja el checkout normal.
       Respeta el locale: parte del href original del botón (que ya incluye el
       root_url con el idioma). Es idempotente y reversible. */
    _syncCheckoutDiscountLink() {
      try {
        const link = this.querySelector('[data-icd-checkout]');
        if (!link) return;
        if (!this._checkoutBaseHref) {
          this._checkoutBaseHref = link.getAttribute('href') || '/checkout';
        }
        const base = this._checkoutBaseHref;
        const code = (this._checkoutDiscountCode || '').trim();
        if (code) {
          const root = base.replace(/checkout\/?$/, '');
          link.setAttribute(
            'href',
            root + 'discount/' + encodeURIComponent(code) +
              '?redirect=' + encodeURIComponent(root + 'checkout')
          );
        } else {
          link.setAttribute('href', base);
        }
      } catch (e) {}
    }

    /* Calcula el bundle activo según el carrito. ÚNICA fuente de verdad: la usan
       el total del pedido, las tarjetas del carrusel y el botón de checkout.
       Idempotente: solo toca el DOM para sincronizar el href del botón de pago.

       FLUJO (lo que pidió el cliente):
       1. Se elige la regla activa por el producto DISPARADOR en el carrito
          (prioridad: reglas por producto 6–10 antes que las de colección 1–5).
          Si ninguna matchea → se usa el fallback de recomendados GENÉRICOS
          (upsellDefault + su % y código).
       2. Las TARJETAS del carrusel muestran el precio ya descontado (incentivo),
          PERO solo si la regla tiene CÓDIGO puesto (si no, precio normal).
       3. El descuento al PEDIDO COMPLETO (total + código en checkout) se activa
          ÚNICAMENTE cuando el cliente ya tiene en el carrito al menos UNO de los
          productos recomendados de esa regla. Con solo el disparador NO se
          aplica todavía. */
    _computeActiveBundle() {
      const cartItems = (this.cart && this.cart.items) || [];
      const bundles = this.config.bundles || [];
      const evalOrder = [5, 6, 7, 8, 9, 0, 1, 2, 3, 4]; // producto → colección

      let targets = null;
      let discountRaw = 0;
      let code = '';
      let ruleIdx = 0;

      for (let k = 0; k < evalOrder.length; k++) {
        const i = evalOrder[k];
        const bundle = bundles[i];
        if (!bundle) continue;
        if (!bundle.handles || bundle.handles.size === 0) continue;
        if (!bundle.targets || bundle.targets.length === 0) continue;
        const matches = cartItems.some(
          (item) => item && item.handle && bundle.handles.has(item.handle)
        );
        if (matches) {
          targets = bundle.targets;
          discountRaw = bundle.discount || 0;
          code = bundle.code || '';
          ruleIdx = i + 1; // 1-based
          break;
        }
      }

      // Fallback: recomendados genéricos (cuando ninguna regla matchea).
      if (!targets) {
        targets = this.config.upsellDefault || [];
        discountRaw = this.config.upsellDefaultDiscount || 0;
        code = this.config.upsellDefaultCode || '';
        ruleIdx = 0;
      }

      const codeSet = !!(code && code.trim());

      // Descuento que se MUESTRA en las tarjetas del carrusel (incentivo).
      // Solo si la regla tiene código (si no, no prometemos un descuento que no
      // se va a aplicar en caja).
      const displayDiscount = codeSet && discountRaw > 0 ? discountRaw : 0;

      // ¿Hay ya en el carrito al menos UN producto recomendado de esta regla?
      const targetInCart = cartItems.some(
        (item) => item && item.handle && targets.indexOf(item.handle) !== -1
      );

      // Descuento al PEDIDO: solo cuando hay un recomendado en el carrito.
      const orderDiscount = displayDiscount > 0 && targetInCart ? displayDiscount : 0;

      this._activeBundleTargets = targets;
      this._activeBundleDiscount = displayDiscount;      // tarjetas del carrusel
      this._activeBundleRuleIdx = ruleIdx;
      this._activeBundleCode = code;
      this._orderDiscountPct = orderDiscount;            // (compat; ya no se usa para el total)
      // El descuento recae SOLO sobre las líneas recomendadas (targets) que estén
      // en el carrito. El disparador y el resto se quedan a precio completo.
      // Coincide con el código "% en estos productos" aplicado en checkout.
      this._targetDiscountPct = displayDiscount;
      // El botón "TRAMITAR PEDIDO" aplica el código SOLO cuando el descuento al
      // pedido está activo (hay un recomendado en el carrito).
      this._checkoutDiscountCode = orderDiscount > 0 ? code.trim() : '';
      this._syncCheckoutDiscountLink();
      this._syncSessionDiscount();
    }

    /* Mantiene la SESIÓN de Shopify en sintonía con el bundle. Si un código de
       bundle se quedó PEGADO en la sesión (p. ej. el cliente pulsó "Tramitar
       pedido" y volvió atrás) pero el bundle ya NO está activo —o cambió— lo
       quita con /cart/update.js {discount:''}. Así, al quitar el disparador, el
       recomendado vuelve a su precio REAL de Shopify tanto en el carrito como en
       caja (carrito = caja SIEMPRE). Nunca toca un código propio del cliente:
       solo retira códigos que pertenecen a las reglas de bundle. */
    _syncSessionDiscount() {
      const cart = this.cart;
      if (!cart || this._removingDiscount) return;
      const applied = (cart.discount_codes || [])
        .filter((dc) => dc && dc.applicable)
        .map((dc) => String(dc.code || '').trim().toUpperCase());
      if (!applied.length) return;
      // Conjunto de códigos que pertenecen a NUESTRAS reglas de bundle.
      const bundleCodes = new Set();
      (this.config.bundles || []).forEach((b) => {
        if (b && b.code) bundleCodes.add(String(b.code).trim().toUpperCase());
      });
      if (this.config.upsellDefaultCode) {
        bundleCodes.add(String(this.config.upsellDefaultCode).trim().toUpperCase());
      }
      const stuck = applied.filter((c) => bundleCodes.has(c));
      if (!stuck.length) return; // es un código del cliente → NO tocar
      // El código correcto para el bundle activo es _checkoutDiscountCode. Si el
      // pegado coincide con ese, está bien; si sobra cualquiera, hay que limpiar.
      const wanted = String(this._checkoutDiscountCode || '').trim().toUpperCase();
      const sobra = stuck.some((c) => c !== wanted) || (!wanted && stuck.length > 0);
      if (!sobra) return;
      this._removingDiscount = true;
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: '' }),
      })
        .then(() => { this._removingDiscount = false; return this._fetchCart(); })
        .catch(() => { this._removingDiscount = false; });
    }

    async _loadUpsells() {
      if (!this.config.showUpsell || !this.$upsell || !this.$upsellCarousel) return;
      // Normalmente _render() ya calculó el bundle activo; si se llama suelto
      // (p.ej. al abrir el drawer) lo calculamos aquí para no usar datos viejos.
      if (this._activeBundleTargets === undefined) this._computeActiveBundle();
      if (!this.cart || !this.cart.items || this.cart.items.length === 0) {
        this.$upsell.hidden = true;
        return;
      }

      const targets = this._activeBundleTargets || this.config.upsellDefault || [];
      if (!targets.length) {
        this.$upsell.hidden = true;
        this.$upsellCarousel.innerHTML = '';
        return;
      }

      // Cache por lista de targets (key = handles unidos). Si cambia, refetch.
      const cacheKey = targets.join('|');
      if (this.upsellsLoadedFor !== cacheKey || !this._cachedUpsells) {
        try {
          const results = await Promise.all(
            targets.map((handle) =>
              fetch(`/products/${handle}.js`, {
                credentials: 'same-origin',
                headers: { Accept: 'application/json' },
              })
                .then((r) => (r.ok ? r.json() : { __failed: true, __handle: handle, __status: r.status }))
                .catch(() => ({ __failed: true, __handle: handle, __status: 'network' }))
            )
          );
          results.forEach((r) => {
            if (r && r.__failed) {
              console.warn(
                `[icd] Upsell handle "${r.__handle}" no se pudo cargar (status ${r.__status}). Revisa que el handle exista.`
              );
            }
          });
          this._cachedUpsells = results.filter((r) => r && !r.__failed);
          this.upsellsLoadedFor = cacheKey;
        } catch (e) {
          console.warn('[icd] _loadUpsells fetch failed', e);
          this.$upsell.hidden = true;
          return;
        }
      }

      // Filtrar: el producto ya en carrito se quita del carrusel.
      const inCart = new Set(this.cart.items.map((i) => i.product_id));
      const filtered = this._cachedUpsells.filter((p) => !inCart.has(p.id));

      if (!filtered.length) {
        this.$upsell.hidden = true;
        this.$upsellCarousel.innerHTML = '';
        this._upsellRenderedSig = null;
        return;
      }

      // Firma del contenido: si ni la lista de productos ni el descuento de
      // bundle (que cambia el precio mostrado) varían, NO reconstruimos el HTML
      // → sin parpadeo de imágenes y se conserva el desplegable "Opciones".
      const sig = filtered.map((p) => p.id).join(',') + '|' +
        (Number(this._activeBundleDiscount) || 0) + '|' +
        (Number(this._activeBundleRuleIdx) || 0);
      this.$upsell.hidden = false;
      if (this._upsellRenderedSig === sig &&
          this.$upsellCarousel.querySelector('.icd-upsell-card')) {
        return;
      }
      this._upsellRenderedSig = sig;
      this.$upsellCarousel.innerHTML = `<div class="icd-upsell-cards-grid">${filtered
        .map((p) => this._upsellCardHTML(p))
        .join('')}</div>`;

      // Tras renderizar, actualizar estado disabled de las flechas
      // (usamos rAF para esperar a que el DOM mida bien)
      requestAnimationFrame(() => this._updateUpsellArrows());
    }

    /* ── Navegación con flechas del carousel de upsell ─────────────── */

    _updateUpsellArrows() {
      if (!this.$upsellCarousel || !this.$upsellPrev || !this.$upsellNext) return;
      const sl = this.$upsellCarousel.scrollLeft;
      const max = this.$upsellCarousel.scrollWidth - this.$upsellCarousel.clientWidth;
      this.$upsellPrev.disabled = sl <= 1;
      this.$upsellNext.disabled = sl >= max - 1;
      // Ocultar las flechas si no hay overflow (no hay scroll posible)
      if (this.$upsellNav) {
        const hasOverflow = this.$upsellCarousel.scrollWidth > this.$upsellCarousel.clientWidth + 2;
        this.$upsellNav.style.visibility = hasOverflow ? 'visible' : 'hidden';
      }
    }

    _flashArrow(btn) {
      // Feedback al tocar la flecha: negro un instante, y se quita a los 2s
      // para que NUNCA se quede el círculo en negro (incluso si el :hover se
      // queda "pegado" en móvil tras el toque).
      if (!btn) return;
      btn.classList.add('icd-upsell-arrow--tapped');
      clearTimeout(btn.__icdFlashTimer);
      btn.__icdFlashTimer = setTimeout(function () {
        btn.classList.remove('icd-upsell-arrow--tapped');
      }, 2000);
    }

    _scrollUpsell(dir) {
      if (!this.$upsellCarousel) return;
      const card = this.$upsellCarousel.querySelector('.icd-upsell-card');
      const w = card ? card.getBoundingClientRect().width + 6 : this.$upsellCarousel.clientWidth * 0.85;
      try {
        this.$upsellCarousel.scrollBy({ left: dir * w, behavior: 'smooth' });
      } catch (e) {
        this.$upsellCarousel.scrollLeft += dir * w;
      }
    }

    _upsellCardHTML(p) {
      const handle = p.handle || '';
      // Caja 75x112 (.icd-upsell-img) -> base 112 (lado mayor)
      const imgBase = p.featured_image || (p.images && p.images[0]) || '';
      const img = sizedImage(imgBase, 112);
      const imgSrcset = srcsetFor(imgBase, 112);
      const url = p.url || `/products/${handle}`;
      const priceNum = Number(p.price) || 0;
      const compareAtNum = Number(p.compare_at_price) || 0;
      const compareAt = compareAtNum > priceNum ? compareAtNum : null;

      // Precio del recomendado en la tarjeta (incentivo para que lo añada):
      // - TACHADO = compare_at_price de Shopify (si existe). No se toca nunca.
      // - PRECIO ROJO (lo que efectivamente pagará dentro del bundle) =
      //   precio de Shopify − % del bundle. _activeBundleDiscount ya está
      //   "blindado" (0 si la regla no tiene código), así que solo se descuenta
      //   visualmente cuando ese descuento se va a aplicar de verdad en caja.
      const bundleDisc = Number(this._activeBundleDiscount) || 0;
      const bundleRuleIdx = Number(this._activeBundleRuleIdx) || 0;
      const originalToTachado = compareAt; // null si no hay compare_at
      // MISMA fórmula de reparto por línea que Shopify (y que _lineItemHTML), para
      // que el precio del carrusel = el de la línea cuando lo añada = el de caja.
      const displayPrice = bundleDisc > 0
        ? priceNum - Math.round(priceNum * bundleDisc / 100)
        : priceNum;

      const priceFormatted = this._formatMoney(displayPrice);
      const originalFormatted = originalToTachado ? this._formatMoney(originalToTachado) : '';
      // Rojo (sale) cuando hay descuento de bundle aplicado O compare_at natural.
      const finalClass = (bundleDisc > 0 || compareAt)
        ? 'icd-upsell-price-final icd-upsell-price-final--sale'
        : 'icd-upsell-price-final';

      // Etiqueta "AHORRA X%": SOLO si el producto tiene precio TACHADO real
      // (compare_at_price > price). `compareAt` ya es null si no lo hay, así que
      // los productos sin tachado NUNCA muestran etiqueta. El % se calcula del
      // tachado al precio mostrado (redondeado), distinto en cada producto.
      const savePct = compareAt ? Math.round((((compareAt - displayPrice) / compareAt) * 100) / 5) * 5 : 0;
      const saveBadge = (this.config.savingsShow && compareAt && savePct > 0)
        ? `<span class="icd-upsell-save">${escapeHTML(this.config.savingsText)} ${savePct}%</span>`
        : '';

      // Variantes: si el producto tiene más de una, mostramos "Opciones" que
      // despliega EN LÍNEA (sin popup) un <select> nativo + botón añadir.
      // Si solo tiene una variante, el botón "+ Añadir" la añade directa.
      const variants = Array.isArray(p.variants) ? p.variants : [];
      const hasOptions = variants.length > 1;
      const firstAvail = variants.find((v) => v.available) || variants[0] || {};
      const addLabel = escapeHTML(this.config.upsellButtonLabel);
      // Interior del botón añadir: etiqueta + tres puntitos de carga (ocultos
      // hasta aria-busy). Reutiliza la animación icd-dot-wave del carrito.
      const addInner =
        `<span class="icd-upsell-add-label">${addLabel}</span>` +
        `<span class="icd-upsell-add-dots" aria-hidden="true"><i></i><i></i><i></i></span>`;
      let addControl;
      if (hasOptions) {
        const optionsHtml = variants
          .map(
            (v) =>
              `<option value="${v.id}"${v.available ? '' : ' disabled'}>${escapeHTML(v.title)}${v.available ? '' : ' (agotado)'}</option>`
          )
          .join('');
        addControl = `
            <button type="button" class="icd-upsell-add icd-upsell-options-btn" data-icd-upsell-options>Opciones</button>
            <div class="icd-upsell-variant" data-icd-upsell-variant hidden>
              <div class="icd-upsell-select-wrap">
                <select class="icd-upsell-select" data-icd-upsell-select aria-label="Elige una opción">${optionsHtml}</select>
                <svg class="icd-upsell-select-arrow" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </div>
              <button type="button" class="icd-upsell-add" data-icd-upsell-add data-icd-bundle-discount="0" data-icd-bundle-rule-idx="${bundleRuleIdx}">${addInner}</button>
            </div>`;
      } else {
        addControl = `
            <button type="button" class="icd-upsell-add" data-icd-upsell-add data-icd-variant-id="${firstAvail.id || ''}" data-icd-bundle-discount="0" data-icd-bundle-rule-idx="${bundleRuleIdx}">${addInner}</button>`;
      }

      return `
        <div class="icd-upsell-card">
          <a class="icd-upsell-img-wrap" href="${escapeHTML(url)}" tabindex="-1">
            <img class="icd-upsell-img"
                 src="${escapeHTML(img)}"
                 srcset="${escapeHTML(imgSrcset)}"
                 alt="${escapeHTML(p.title)}"
                 width="112" height="112" loading="lazy" decoding="async">
          </a>
          <div class="icd-upsell-info">
            <a class="icd-upsell-name" href="${escapeHTML(url)}">${escapeHTML(p.title)}</a>
            <div class="icd-upsell-prices">
              <span class="${finalClass}">${priceFormatted}</span>
              ${originalToTachado ? `<span class="icd-upsell-price-original">${originalFormatted}</span>` : ''}
              ${saveBadge}
            </div>
            ${addControl}
          </div>
        </div>`;
    }

    /* ── Cart mutations ──────────────────────────────────────────────── */

    /**
     * Cambia la cantidad de una línea concreta.
     *
     * Usa SIEMPRE `line` (1-based index) — más fiable que `id: key` cuando
     * la línea tiene line item properties (ej: `_bundle_promo`). Con `id`,
     * Shopify a veces interpreta solo el variant_id y duplica líneas en
     * lugar de actualizar la qty existente. `line` no tiene ese problema.
     *
     * `key` se acepta opcionalmente solo para `_setLineBusy` durante la
     * petición; no se envía al endpoint.
     */
    /**
     * Cambia la cantidad de una línea concreta. BULLETPROOF:
     *   - Retry con backoff exponencial (3 intentos) en errores de red.
     *   - Fallback a _fetchCart si todo falla → UI se resincroniza con
     *     el server, el cliente nunca queda con estado inconsistente.
     *   - unlock GARANTIZADO en finally (incluso si el JS dispara
     *     excepción no capturada).
     *   - Tolerante a Cloudflare 5xx, timeouts y RST de conexión móvil.
     */
    // Serializa las mutaciones de carrito (una a una). Así el re-derive de índice
    // ve el carrito YA actualizado por la mutación anterior y dos taps rápidos en
    // líneas distintas no se pisan (antes el 2º borrado se perdía). La cola nunca
    // se rompe: los rechazos se tragan para no bloquear las siguientes.
    _serialize(fn) {
      const next = (this._mutationChain || Promise.resolve()).then(fn, fn);
      this._mutationChain = next.catch(() => {});
      return next;
    }

    _changeQty(lineIdx, qty, key) {
      return this._serialize(() => this._changeQtyRun(lineIdx, qty, key));
    }

    async _changeQtyRun(lineIdx, qty, key) {
      // Re-derivar el índice REAL desde la key contra el carrito ACTUAL: evita la
      // carrera de índices caducados si otra línea se quitó/cambió justo antes
      // (p.ej. quitar B y luego C: C ya no está en el índice con el que se pintó).
      if (key && this.cart && Array.isArray(this.cart.items)) {
        const cur = this.cart.items.findIndex((i) => i.key === key);
        if (cur === -1) { this._unlockLine(key); this._setLineBusy(key, false); return; }
        lineIdx = cur + 1;
      }
      const body = (typeof lineIdx === 'number' && lineIdx > 0)
        ? { line: lineIdx, quantity: qty }
        : { id: lineIdx, quantity: qty }; // fallback retro-compat

      const attempt = async () => {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const txt = await res.text().catch(() => '');
          throw new Error(`change.js ${res.status}: ${txt.slice(0, 200)}`);
        }
        return res.json();
      };

      let lastErr = null;
      this._internalFetchActive++;
      try {
        for (let i = 0; i < 3; i++) {
          try {
            const data = await attempt();
            this.cart = data;
            await this._revalidateBundleDiscounts();
            this._scheduleRender();
            return;
          } catch (e) {
            lastErr = e;
            if (i < 2) {
              // backoff: 100ms, 400ms
              await new Promise((r) => setTimeout(r, i === 0 ? 100 : 400));
            }
          }
        }
        console.warn('[icd] _changeQty failed after retries:', lastErr);
        // Última red de seguridad: resync completo desde servidor.
        await this._fetchCart();
      } finally {
        this._internalFetchActive = Math.max(0, this._internalFetchActive - 1);
        this._unlockLine(key);
        this._setLineBusy(key, false);
      }
    }

    /**
     * Añade una variante al cart. BULLETPROOF: retry x3 con backoff,
     * inyecta line item properties si hay bundle activo, limpia el
     * estado bundle pending tras éxito, y SIEMPRE resincroniza el
     * cart al final (éxito o fallo) para evitar UI desincronizada.
     */
    _addVariant(variantId) {
      // Capturamos el descuento de bundle pendiente AL ENCOLAR (no al ejecutar),
      // para que dos adds seguidos no se mezclen el % al serializarse.
      const disc = this._pendingBundleDiscount;
      const ruleIdx = this._pendingBundleRuleIdx;
      return this._serialize(() => {
        this._pendingBundleDiscount = disc;
        this._pendingBundleRuleIdx = ruleIdx;
        return this._addVariantRun(variantId);
      });
    }

    async _addVariantRun(variantId) {
      const body = { id: variantId, quantity: 1 };
      // Si hay un descuento de bundle pendiente (capturado en el click
      // del upsell), lo inyectamos como line item properties:
      //   _bundle_promo: "20%"  → marca para descuento automático de Shopify
      //   _bundle_rule:  "1"    → índice (1..5) del rule que disparó el bundle
      const pendingDisc = Number(this._pendingBundleDiscount) || 0;
      const pendingRuleIdx = Number(this._pendingBundleRuleIdx) || 0;
      if (pendingDisc > 0) {
        body.properties = { _bundle_promo: pendingDisc + '%' };
        if (pendingRuleIdx > 0) {
          body.properties._bundle_rule = String(pendingRuleIdx);
        }
      }

      const attempt = async () => {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const txt = await res.text().catch(() => '');
          throw new Error(`add.js ${res.status}: ${txt.slice(0, 200)}`);
        }
        return res.json();
      };

      let success = false;
      let lastErr = null;
      this._internalFetchActive++;
      try {
        for (let i = 0; i < 3; i++) {
          try {
            await attempt();
            success = true;
            break;
          } catch (e) {
            lastErr = e;
            if (i < 2) await new Promise((r) => setTimeout(r, i === 0 ? 100 : 400));
          }
        }
        if (success) {
          this._pendingBundleDiscount = 0;
          this._pendingBundleRuleIdx = 0;
        } else {
          console.warn('[icd] _addVariant failed after retries:', lastErr);
        }
        // Resync siempre — incluso si falló, refresca por si el server SÍ
        // recibió la petición (típico en timeouts WebView).
        await this._fetchCart({ autoOpen: success });
      } finally {
        this._internalFetchActive = Math.max(0, this._internalFetchActive - 1);
      }
    }

    /**
     * Revalida los descuentos de bundle en cada cambio de carrito.
     *
     * Caso de uso (descrito por el cliente):
     *   1. Cliente añade VESTIDO (producto disparador del bundle).
     *   2. En el upsell aparece TACÓN con −20%. Cliente lo añade → tacón
     *      entra con properties `_bundle_promo: "20%"` y `_bundle_rule: "1"`.
     *   3. Cliente cambia de opinión y QUITA el vestido del carrito.
     *   4. Sin esta función: el tacón seguiría con −20% aunque el motivo
     *      del descuento (el vestido) ya no esté.
     *   5. CON esta función: detectamos que rule 1 ya no tiene ningún producto
     *      trigger en el carrito → eliminamos las properties del tacón →
     *      vuelve al precio normal automáticamente.
     *
     * Se invoca desde `_fetchCart` (después de cada cambio del carrito).
     * No hace nada si no encuentra desajustes (no-op rápido).
     *
     * Retorna `true` si aplicó algún cambio (útil si se quisiera encadenar).
     */
    /**
     * Síncrono. Decide si un line item con _bundle_promo TODAVÍA puede
     * mostrar el descuento del bundle, basándose en el estado actual del
     * cart. Misma lógica que _revalidateBundleDiscounts pero solo lectura.
     *
     * Se usa en `_lineItemHTML` como BLINDAJE visual: aunque el property
     * `_bundle_promo` persista en el cart (porque la llamada de cleanup
     * aún no terminó o falló), el drawer mostrará el precio normal si ya
     * no hay trigger.
     */
    _isBundleStillValid(item) {
      if (!this.cart || !this.cart.items || !this.cart.items.length) return false;
      const rules = (this.config && this.config.bundles) || [];
      if (!rules.length) return false;
      const props = (item && item.properties) || {};
      if (!props._bundle_promo) return false;

      // Determina qué reglas hay que comprobar (mismo algoritmo que
      // _revalidateBundleDiscounts, simplificado).
      const ruleIdxProp = parseInt(props._bundle_rule, 10);
      let rulesToCheck;
      if (ruleIdxProp && ruleIdxProp >= 1 && ruleIdxProp <= rules.length) {
        rulesToCheck = [rules[ruleIdxProp - 1]];
      } else {
        // Legacy: usa todas las reglas que tienen este producto como target
        rulesToCheck = rules.filter(
          (r) => r && r.targets && item.handle && r.targets.indexOf(item.handle) !== -1
        );
        if (!rulesToCheck.length) rulesToCheck = rules;
      }
      rulesToCheck = rulesToCheck.filter((r) => r && r.handles && r.handles.size > 0);
      if (!rulesToCheck.length) return false;

      const items = this.cart.items;
      const selfIdx = items.indexOf(item);
      return rulesToCheck.some((rule) =>
        items.some((other, oIdx) => {
          if (oIdx === selfIdx) return false;
          return other && other.handle && rule.handles.has(other.handle);
        })
      );
    }

    async _revalidateBundleDiscounts() {
      if (!this.cart || !this.cart.items || !this.cart.items.length) return false;
      const rules = (this.config && this.config.bundles) || [];
      if (!rules.length) return false;

      const items = this.cart.items;

      // Pre-cálculo: union de TODOS los triggers (handles) y un mapa
      // handle→Set(ruleIdx) de qué reglas pueden dar descuento a qué
      // productos (por estar en sus targets). Esto permite revalidar
      // incluso items LEGACY que no tienen _bundle_rule.
      const allTriggerHandles = new Set();
      const targetToRules = new Map(); // handle (target) → Set<ruleIdx 1-based>
      rules.forEach((rule, i) => {
        if (rule.handles && rule.handles.size) {
          rule.handles.forEach((h) => allTriggerHandles.add(h));
        }
        if (rule.targets && rule.targets.length) {
          rule.targets.forEach((h) => {
            if (!targetToRules.has(h)) targetToRules.set(h, new Set());
            targetToRules.get(h).add(i + 1);
          });
        }
      });
      if (allTriggerHandles.size === 0) return false; // ningún rule configurado

      const changes = []; // [{ line: 1-based, properties: {...sin promo} }]

      items.forEach((item, idx) => {
        const props = (item && item.properties) || {};
        if (!props._bundle_promo) return;          // línea sin promo, ignorar

        // Decidir qué reglas hay que comprobar para esta línea:
        //   1) Si tiene `_bundle_rule` válido → solo esa regla concreta.
        //   2) Si no (item LEGACY añadido antes de v47) → todas las reglas
        //      cuyos `targets` contengan el handle de este producto.
        //   3) Si no encajamos en ninguna → cualquier regla con triggers
        //      (fallback más permisivo, casi nunca llega aquí).
        const ruleIdxProp = parseInt(props._bundle_rule, 10);
        let rulesToCheck;
        if (ruleIdxProp && ruleIdxProp >= 1 && ruleIdxProp <= rules.length) {
          rulesToCheck = [rules[ruleIdxProp - 1]];
        } else {
          const targetSet = (item.handle && targetToRules.get(item.handle)) || null;
          if (targetSet && targetSet.size) {
            rulesToCheck = [...targetSet].map((n) => rules[n - 1]);
          } else {
            rulesToCheck = rules;
          }
        }
        rulesToCheck = rulesToCheck.filter(
          (r) => r && r.handles && r.handles.size > 0
        );
        if (!rulesToCheck.length) return;

        // ¿Hay AÚN en el carrito algún OTRO item cuyo handle esté en los
        // triggers de ALGUNA de las reglas candidatas? Si sí → la promo
        // todavía está justificada; si no → la promo es huérfana → strip.
        const stillHasTrigger = rulesToCheck.some((rule) =>
          items.some((other, oIdx) => {
            if (oIdx === idx) return false;
            return other && other.handle && rule.handles.has(other.handle);
          })
        );
        if (stillHasTrigger) return;

        // No queda trigger → quitar las properties de bundle.
        // Conservamos cualquier OTRA property que el cliente pueda haber
        // añadido manualmente (engraving, nota, etc).
        const newProps = {};
        Object.keys(props).forEach((k) => {
          if (k !== '_bundle_promo' && k !== '_bundle_rule') {
            newProps[k] = props[k];
          }
        });
        changes.push({ line: idx + 1, properties: newProps });
      });

      if (!changes.length) return false;

      // Aplicar cambios secuencialmente. `/cart/change.js` mantiene los
      // índices de línea cuando solo modificamos `properties` (no
      // cambia el orden, ni quita la línea), así que es seguro iterar.
      // Cada llamada devuelve el carrito completo → vamos actualizando
      // this.cart sobre la marcha para que el siguiente cambio vea el
      // estado correcto.
      for (const c of changes) {
        try {
          const res = await fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify(c),
          });
          if (res.ok) {
            this.cart = await res.json();
          }
        } catch (e) {
          console.warn('[icd] _revalidateBundleDiscounts failed', e);
        }
      }
      return true;
    }

    // Cache de productos completos, indexado por handle. Lo usamos para
    // obtener compare_at_price cuando la variante específica del carrito
    // no lo tiene seteado (sí lo tiene a nivel de otras variantes/producto).
    async _fetchMissingProducts() {
      if (!this.cart || !this.cart.items) return;
      this._productCache = this._productCache || {};
      const toFetch = this.cart.items.filter(
        (item) => item.handle && !Object.prototype.hasOwnProperty.call(this._productCache, item.handle)
      );
      if (!toFetch.length) return;
      await Promise.all(
        toFetch.map(async (item) => {
          try {
            const r = await fetch(`/products/${item.handle}.js`, {
              credentials: 'same-origin',
              headers: { Accept: 'application/json' },
            });
            this._productCache[item.handle] = r.ok ? await r.json() : null;
          } catch (e) {
            this._productCache[item.handle] = null;
          }
        })
      );
    }

    // Devuelve el compare_at_price efectivo en cents para esta línea de carrito.
    // Si la variante en sí no tiene compare_at, busca el máximo entre todas las
    // variantes del producto. Devuelve 0 si no encuentra ninguno mayor que price.
    _getEffectiveCompareAt(item) {
      const itemPrice = Number(item.final_price != null ? item.final_price : item.price) || 0;
      const variantCompare = Number(item.compare_at_price) || 0;
      if (variantCompare > itemPrice) return variantCompare;

      this._productCache = this._productCache || {};
      const product = this._productCache[item.handle];
      if (!product || !product.variants) return 0;

      // Usar el compare_at de la VARIANTE EXACTA de esta linea (por id), NO el maximo
      // entre todas las variantes: en un producto con packs (cada pack = variante con su
      // propio compare) el maximo daba el tachado del pack mas caro a cualquier linea
      // (p.ej. la linea "2 cojines" mostraba 4.398 = compare del pack 3 en MXN).
      const vid = Number(item.variant_id || item.id) || 0;
      const v = product.variants.find((x) => Number(x.id) === vid);
      const c = v ? (Number(v.compare_at_price) || 0) : 0;
      return c > itemPrice ? c : 0;
    }

    /* ── Packs del cojin (1/2/3 cojines = 3 variantes) ─────────────────
       El selector de cantidad de un pack muestra el Nº DE COJINES (1, 2 o 3) y +/- cambian de
       PACK (variante), no suman unidades de la misma. IDs del producto (fallback fijo si no hay
       global). count se toma del propio tier. */
    _packTiers() {
      // PACKS MIGRADOS A KACHING (23-09): ya no hay variantes-pack (solo "1 cojin" + cantidad
      // real; "2 cojines"/"3 cojines" borradas). Devolvemos [] => isPack SIEMPRE false =>
      // el carrito lateral muestra la CANTIDAD REAL (item.quantity) y los +/- son un stepper
      // normal. Antes el fallback fijo remapeaba "1 cojin" a count:1, asi que el Pack 2 de
      // Kaching (2 uds de "1 cojin") se veia como "1 cojin". Reactivar solo si se vuelve a
      // vender por variantes-pack.
      return [];
    }
    _packTierIndex(variantId) {
      const t = this._packTiers();
      for (let i = 0; i < t.length; i++) if (Number(t[i].id) === Number(variantId)) return i;
      return -1;
    }
    /* Total de COJINES en el carrito: packs (1/2/3 uds por variante) + cojines sueltos (_unit).
       El regalo (funda) entra si hay 2 o mas. Excluye la propia funda. */
    _totalCushions(cart) {
      const FUNDA = 59221467136345;
      const tiers = this._packTiers();
      const tierOf = (id) => { for (let i = 0; i < tiers.length; i++) if (Number(tiers[i].id) === Number(id)) return tiers[i]; return null; };
      return (cart.items || []).reduce((s, i) => {
        if (Number(i.variant_id) === FUNDA) return s;
        const isUnit = i.properties && (i.properties._unit || i.properties['_unit']);
        if (isUnit) return s + (i.quantity || 0);
        const t = tierOf(i.variant_id);
        return s + (t ? (t.count || 1) * (i.quantity || 0) : 0);
      }, 0);
    }
    /* Anade UN cojin suelto (unidad extra) por encima del pack maximo: variante "1 cojin" marcada
       con _unit para que sea una linea de cantidad normal (no un pack). Gestiona el regalo y pinta
       UNA sola vez (mismo patron que _switchPackTier: suprime reconciliador + interceptor). */
    _addExtraUnit(key) {
      return this._serialize(async () => {
        const FUNDA = 59221467136345;
        const UNIT = 59221635432793;
        const before = window.__somSkipReconcile;
        window.__somSkipReconcile = true;
        this._internalFetchActive++;
        const getCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => { if (!r.ok) throw 0; return r.json(); });
        const post = (url, body) => fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then((r) => { if (!r.ok) throw 0; return r.json(); });
        const norm = async (cart) => {
          const qualifies = this._totalCushions(cart) >= 2;
          const gifts = (cart.items || []).filter((i) => Number(i.variant_id) === FUNDA && i.properties && (i.properties._gift || i.properties['_gift']));
          if (qualifies && gifts.length === 0) { await post('/cart/add.js', { items: [{ id: FUNDA, quantity: 1, properties: { _gift: '1' } }] }); cart = await getCart(); }
          else if (qualifies && gifts.length > 1) { for (let n = 1; n < gifts.length; n++) { const p = (cart.items || []).findIndex((it) => it.key === gifts[n].key); if (p >= 0) cart = await post('/cart/change.js', { line: p + 1, quantity: 0 }); } }
          else if (!qualifies && gifts.length > 0) { for (let n = 0; n < gifts.length; n++) { const p = (cart.items || []).findIndex((it) => it.key === gifts[n].key); if (p >= 0) cart = await post('/cart/change.js', { line: p + 1, quantity: 0 }); } }
          return cart;
        };
        const paint = (cart) => {
          this.cart = cart;
          this._renderLineItems();
          this._renderTotals();
          if (typeof this._renderShipping === 'function') this._renderShipping();
          if (typeof this._syncHeaderCount === 'function') this._syncHeaderCount((cart.items || []).reduce((s, i) => s + (i.quantity || 0), 0));
        };
        try {
          await post('/cart/add.js', { items: [{ id: UNIT, quantity: 1, properties: { _unit: '1' } }] });
          let cart = await getCart();
          cart = await norm(cart);
          paint(cart);
        } catch (e) {
          try { paint(await norm(await getCart())); } catch (e2) {}
        }
        this._internalFetchActive = Math.max(0, this._internalFetchActive - 1);
        window.__somSkipReconcile = before || false;
        this._fetchSeq = (this._fetchSeq || 0) + 1;
        if (key) { this._unlockLine(key); this._setLineBusy(key, false); }
      });
    }
    /* Cambia una linea de pack a otra variante de pack (o la quita). Quita la actual por indice de
       linea (fiable) y anade la nueva. El reconciliador del regalo (funda) se ocupa solo. */
    _switchPackTier(lineIdx, key, targetVariantId) {
      return this._serialize(async () => {
        const FUNDA = 59221467136345;
        const before = window.__somSkipReconcile;
        // El drawer gestiona pack + REGALO en bloque y pinta UNA sola vez -> sin parpadeo ni doble
        // render. Se suprime el reconciliador global (__somSkipReconcile) Y el interceptor de fetch del
        // propio drawer (_internalFetchActive++) para que NINGUNA de las peticiones intermedias
        // (quitar pack, anadir pack, ajustar funda) dispare un _fetchCart -> nada de estados
        // intermedios ("carrito vacio", funda saltando de sitio, etc.). Solo el render final de abajo.
        window.__somSkipReconcile = true;
        this._internalFetchActive++;
        // Peticiones con comprobacion de exito (res.ok): si una falla, se lanza y se resincroniza,
        // NUNCA se deja el carrito a medias (regalo huerfano o pack perdido).
        const getCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => { if (!r.ok) throw 0; return r.json(); });
        const post = (url, body) => fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then((r) => { if (!r.ok) throw 0; return r.json(); });
        // Deja la funda de REGALO coherente con el pack REALMENTE presente en el carrito (no con el
        // "deseado"): asi aunque un add fallara o no hubiera stock, jamas queda un regalo sin pack.
        const norm = async (cart) => {
          const qualifies = this._totalCushions(cart) >= 2;
          const gifts = (cart.items || []).filter((i) => i.variant_id === FUNDA && i.properties && (i.properties._gift || i.properties['_gift']));
          if (qualifies && gifts.length === 0) {
            await post('/cart/add.js', { items: [{ id: FUNDA, quantity: 1, properties: { _gift: '1' } }] });
            cart = await getCart();
          } else if (qualifies && gifts.length > 1) {
            for (let n = 1; n < gifts.length; n++) { const p = (cart.items || []).findIndex((it) => it.key === gifts[n].key); if (p >= 0) cart = await post('/cart/change.js', { line: p + 1, quantity: 0 }); }
          } else if (!qualifies && gifts.length > 0) {
            for (let n = 0; n < gifts.length; n++) { const p = (cart.items || []).findIndex((it) => it.key === gifts[n].key); if (p >= 0) cart = await post('/cart/change.js', { line: p + 1, quantity: 0 }); }
          }
          return cart;
        };
        const paint = (cart) => {
          this.cart = cart;
          this._renderLineItems();
          this._renderTotals();
          if (typeof this._renderShipping === 'function') this._renderShipping();
          if (typeof this._syncHeaderCount === 'function') this._syncHeaderCount((cart.items || []).reduce((s, i) => s + (i.quantity || 0), 0));
        };
        try {
          // 1. quitar el pack actual (linea localizada por key -> indice fresco, robusto)
          let cart = await getCart();
          let pos = (cart.items || []).findIndex((it) => it.key === key);
          cart = await post('/cart/change.js', { line: pos >= 0 ? pos + 1 : lineIdx, quantity: 0 });
          // 2. anadir el nuevo pack (si hay destino; si no, es un "quitar")
          if (targetVariantId) { await post('/cart/add.js', { items: [{ id: Number(targetVariantId), quantity: 1 }] }); cart = await getCart(); }
          // 3. regalo coherente con el pack real + 4. UN SOLO render final
          cart = await norm(cart);
          paint(cart);
        } catch (e) {
          // Fallo de red a mitad -> resincroniza desde servidor y normaliza el regalo (sin huerfanos).
          try { paint(await norm(await getCart())); } catch (e2) {}
        }
        this._internalFetchActive = Math.max(0, this._internalFetchActive - 1);
        window.__somSkipReconcile = before || false;
        // Invalida cualquier _fetchCart EXTERNO en vuelo (pageshow/visibilitychange) para que su
        // respuesta, con el estado PRE-switch, no pise el render fresco que acabamos de pintar.
        this._fetchSeq = (this._fetchSeq || 0) + 1;
        this._unlockLine(key);
        this._setLineBusy(key, false);
      });
    }

    /* ── Event binding ───────────────────────────────────────────────── */

    _bindEvents() {
      // Idempotencia: evita re-enganchar listeners (y dobles disparos de clic) si
      // connectedCallback se ejecuta más de una vez sobre el MISMO nodo (el tema
      // reinserta DOM con su navegación morph).
      if (this._eventsBound) return;
      this._eventsBound = true;
      // Close: click on [data-icd-close] inside the drawer or its overlay
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-icd-close]')) {
          this.close();
        }
      });

      // ── Navegación BLINDADA del NOMBRE del producto (líneas + recomendados) ──
      // El tema tiene "navegación instantánea" (morph) que intercepta los clics
      // de enlaces y CANCELA estos, porque están dentro del cajón flotante → el
      // cliente pulsaba el nombre y no iba a ningún lado. Forzamos la navegación
      // nosotros, en fase de CAPTURA, para que SIEMPRE lleve a su página.
      document.addEventListener('click', (e) => {
        const nameLink = e.target && e.target.closest && e.target.closest('.icd-li-title, .icd-upsell-name');
        if (!nameLink) return;
        const href = nameLink.getAttribute('href');
        if (!href || href === '#') return;
        e.preventDefault();
        e.stopPropagation();
        try { window.location.assign(href); return; } catch (err) {}
        try { window.location.href = href; } catch (err) {}
      }, true);

      // ── Flechas del carousel de upsell ──
      if (this.$upsellPrev) {
        this.$upsellPrev.addEventListener('click', (e) => {
          e.preventDefault();
          this._flashArrow(this.$upsellPrev);
          this._scrollUpsell(-1);
        });
      }
      if (this.$upsellNext) {
        this.$upsellNext.addEventListener('click', (e) => {
          e.preventDefault();
          this._flashArrow(this.$upsellNext);
          this._scrollUpsell(1);
        });
      }
      if (this.$upsellCarousel) {
        // Actualiza estado disabled mientras el usuario hace swipe/scroll
        this.$upsellCarousel.addEventListener('scroll', () => {
          this._updateUpsellArrows();
        }, { passive: true });
      }
      // Resize: re-evaluar visibility de las flechas (ocultar si no hay overflow)
      window.addEventListener('resize', () => {
        this._updateUpsellArrows();
      }, { passive: true });

      // External open triggers
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-icd-open]');
        if (opener) {
          e.preventDefault();
          this.open();
          return;
        }
        // Fallback: clicks on cart-icon elements not wrapped in [data-icd-open]
        const cartIconLink = e.target.closest('cart-icon');
        if (cartIconLink) {
          const link = cartIconLink.closest('a[href]');
          if (link && /\/cart\/?$/.test(link.getAttribute('href') || '')) {
            e.preventDefault();
            this.open();
          }
        }
      });

      // ESC key — QB takes priority sobre cart drawer
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const qb = document.querySelector('[data-icd-qb]');
        if (qb && qb.hasAttribute('data-icd-qb-open')) {
          this._closeQuickBuy();
        } else if (this.isOpen) {
          this.close();
        }
      });

      // Refrescar carrito al volver desde navegación (back/forward) o al volver
      // a la pestaña. Garantiza que el upsell siempre refleja el estado real.
      window.addEventListener('pageshow', () => {
        this._fetchCart().catch(() => {});
      });
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this._fetchCart().catch(() => {});
      });

      // === Botones de navegación CRÍTICOS (TRAMITAR PEDIDO + COMPRAR TODO) ===
      // Estrategia BULLETPROOF: 4 capas de redundancia para que la navegación
      // funcione en TODOS los entornos posibles:
      //   1. Native <a href> — funciona por defecto en navegadores normales.
      //   2. JS unlock scroll — evita interferencia del body position:fixed.
      //   3. window.location.assign + .href + .replace — múltiples métodos.
      //   4. setTimeout backup — re-intenta navegación si el primer attempt
      //      no disparó (raro pero pasa en algunos WebViews de Instagram/FB).
      //   Resultado: el cliente puede pulsar infinidad de veces, nunca falla.
      const self = this;
      const bulletproofNavigate = (href) => {
        if (!href) return;
        try { window.location.assign(href); return; } catch (e) {}
        try { window.location.href = href; return; } catch (e) {}
        try { window.location.replace(href); return; } catch (e) {}
        try {
          const tmp = document.createElement('a');
          tmp.href = href;
          tmp.target = '_self';
          tmp.rel = 'noopener';
          document.body.appendChild(tmp);
          tmp.click();
          document.body.removeChild(tmp);
        } catch (e) {}
      };

      document.addEventListener('click', (e) => {
        const navBtn = e.target.closest('[data-icd-checkout], .icd-empty-button');
        if (!navBtn) return;

        e.preventDefault();
        try { self._unlockScroll(); } catch (err) {}

        const isCheckout = navBtn.matches('[data-icd-checkout]');
        const fallbackHref = isCheckout ? '/checkout' : '/';
        const href = navBtn.getAttribute('href') || fallbackHref;

        // Intento primario inmediato
        bulletproofNavigate(href);

        // Backup re-intento a 80ms PERO con cancelación si la navegación primaria
        // ya ha funcionado (pagehide/beforeunload dispara antes del timer).
        // Esto evita la "doble navegación" que en WebViews lentos podía
        // interrumpir el checkout. ⚠️ CRÍTICO para fiabilidad del botón.
        const fallbackTimerId = setTimeout(() => bulletproofNavigate(href), 80);
        const cancelFallback = () => {
          clearTimeout(fallbackTimerId);
          window.removeEventListener('pagehide', cancelFallback);
          window.removeEventListener('beforeunload', cancelFallback);
        };
        window.addEventListener('pagehide', cancelFallback, { once: true });
        window.addEventListener('beforeunload', cancelFallback, { once: true });
      });

      // === Eventos del Quick Buy popup (escucha a nivel document, está fuera) ===
      document.addEventListener('click', (e) => {
        if (e.target.closest && e.target.closest('[data-icd-qb-close]')) {
          this._closeQuickBuy();
        }
      });
      document.addEventListener('change', (e) => {
        if (e.target.matches && e.target.matches('input[data-icd-qb-option]')) {
          this._updateQuickBuyVariant();
        }
      });
      document.addEventListener('submit', (e) => {
        if (e.target.matches && e.target.matches('[data-icd-qb-form]')) {
          e.preventDefault();
          this._submitQuickBuy();
        }
      });

      // Internal +/-/remove/upsell clicks
      // BULLETPROOF: lock global por línea (`_pendingLines`) evita TODA
      // race condition cuando el cliente martillea +/− muy rápido o cuando
      // el upsell se clickea múltiples veces antes de que abra el popup.
      // Las operaciones de la misma línea se descartan si hay otra en
      // curso — la última gana porque tras el render se ve la qty real
      // del cart y el cliente puede pulsar de nuevo sobre el estado fresco.
      this.addEventListener('click', (e) => {
        const incBtn = e.target.closest('[data-icd-qty-inc]');
        const decBtn = e.target.closest('[data-icd-qty-dec]');
        const remBtn = e.target.closest('[data-icd-remove]');
        const upsellAdd = e.target.closest('[data-icd-upsell-add]');
        const upsellOptions = e.target.closest('[data-icd-upsell-options]');

        // Si el botón está disabled, ignorar — evita doble-trigger en
        // dispositivos táctiles que disparan click + touchend juntos.
        const anyBtn = incBtn || decBtn || remBtn || upsellAdd || upsellOptions;
        if (anyBtn && (anyBtn.disabled || anyBtn.hasAttribute('aria-busy'))) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        if (incBtn) {
          const key = incBtn.dataset.icdLineKey;
          if (this._isLinePending(key)) return; // otra operación en curso
          const lineIdx = parseInt(incBtn.dataset.icdLineIndex, 10);
          // PACK: '+' SUBE de pack (1->2->3 cojines). En el tope el boton va disabled -> sin efecto.
          if (incBtn.dataset.icdPack) {
            const nextId = incBtn.dataset.icdPackNext;
            this._lockLine(key);
            this._setLineBusy(key, true);
            if (nextId) {
              // Sube de pack (1->2->3 cojines).
              this._switchPackTier(lineIdx, key, nextId);
            } else {
              // Tope de packs (3 cojines): '+' anade UN cojin suelto mas (unidad), sin limite.
              this._addExtraUnit(key);
            }
            return;
          }
          const qty = parseInt(incBtn.dataset.icdQty, 10) || 1;
          this._lockLine(key);
          this._setLineBusy(key, true);
          this._changeQty(lineIdx, qty + 1, key);
        } else if (decBtn) {
          const key = decBtn.dataset.icdLineKey;
          if (this._isLinePending(key)) return;
          const lineIdx = parseInt(decBtn.dataset.icdLineIndex, 10);
          // PACK: '-' BAJA de pack (3->2->1 cojines); en "1 cojin" quita la linea del carrito.
          if (decBtn.dataset.icdPack) {
            const prevId = decBtn.dataset.icdPackPrev;
            this._lockLine(key);
            this._setLineBusy(key, true);
            // Baja de tier (prevId) o, en "1 cojin", quita la linea (targetVariantId null).
            // Ambos por _switchPackTier -> gestiona el regalo y pinta UNA vez (sin saltos).
            this._switchPackTier(lineIdx, key, prevId || null);
            return;
          }
          const qty = parseInt(decBtn.dataset.icdQty, 10) || 1;
          this._lockLine(key);
          this._setLineBusy(key, true);
          this._changeQty(lineIdx, Math.max(0, qty - 1), key);
        } else if (remBtn) {
          const key = remBtn.dataset.icdLineKey;
          if (this._isLinePending(key)) return;
          const lineIdx = parseInt(remBtn.dataset.icdLineIndex, 10);
          this._lockLine(key);
          this._setLineBusy(key, true);
          // Si es un PACK, se borra por _switchPackTier(null): quita pack + su regalo en UN solo
          // render (sin doble pintado del reconciliador). El resto de productos, borrado normal.
          if (remBtn.dataset.icdPack) this._switchPackTier(lineIdx, key, null);
          else this._changeQty(lineIdx, 0, key);
        } else if (upsellOptions) {
          // Producto con varias variantes: desplegar EN LÍNEA el selector
          // (sin popup). Ocultamos "Opciones" y mostramos el <select> + añadir.
          const card = upsellOptions.closest('.icd-upsell-card');
          const box = card && card.querySelector('[data-icd-upsell-variant]');
          if (box) box.hidden = false;
          upsellOptions.hidden = true;
        } else if (upsellAdd) {
          // Capturamos el descuento del bundle activo (line item property) y
          // añadimos la variante DIRECTA, sin abrir ninguna ventana.
          const disc = parseInt(upsellAdd.dataset.icdBundleDiscount || '0', 10) || 0;
          const ruleIdx = parseInt(upsellAdd.dataset.icdBundleRuleIdx || '0', 10) || 0;
          // Variante: del <select> hermano (multi color) o del data-attr (única).
          let variantId = upsellAdd.dataset.icdVariantId || '';
          const box = upsellAdd.closest('[data-icd-upsell-variant]');
          if (box) {
            const sel = box.querySelector('[data-icd-upsell-select]');
            if (sel) variantId = sel.value;
          }
          if (variantId) {
            this._pendingBundleDiscount = disc;
            this._pendingBundleRuleIdx = ruleIdx;
            upsellAdd.disabled = true;
            upsellAdd.setAttribute('aria-busy', 'true');
            this._addVariant(variantId).finally(() => {
              upsellAdd.disabled = false;
              upsellAdd.removeAttribute('aria-busy');
            });
          }
        }
      });
    }

    /* ── Locking per-line ────────────────────────────────────────────── */

    _isLinePending(key) {
      if (!this._pendingLines) this._pendingLines = new Set();
      return this._pendingLines.has(key);
    }

    _lockLine(key) {
      if (!this._pendingLines) this._pendingLines = new Set();
      this._pendingLines.add(key);
    }

    _unlockLine(key) {
      if (this._pendingLines) this._pendingLines.delete(key);
    }

    _setLineBusy(key, busy) {
      const sel = `[data-icd-line-key="${(window.CSS && CSS.escape) ? CSS.escape(key) : key}"]`;
      this.querySelectorAll(sel).forEach((el) => {
        if (busy) {
          el.setAttribute('disabled', 'true');
          el.setAttribute('aria-busy', 'true');
        } else {
          el.removeAttribute('disabled');
          el.removeAttribute('aria-busy');
        }
      });
    }

    /* ── Fetch/XHR interception for auto-open ─────────────────────────── */

    _interceptCart() {
      const self = this;
      self._lastUserAddClick = self._lastUserAddClick || 0;

      // Registrar SIEMPRE la instancia viva. Los parches globales (fetch/XHR/
      // eventos del tema) se instalan UNA sola vez y enrutan a esta referencia,
      // así una sola instalación sirve a cualquier instancia tras navegaciones
      // morph: NO se apilan wrappers de fetch ni listeners de documento.
      window.__icdCartInstance = self;
      if (window.__icdCartGlobalPatched) return;
      window.__icdCartGlobalPatched = true;

      const live = function () { return window.__icdCartInstance; };
      // ¿Debe el interceptor relanzar un refresco? Solo si hay instancia viva y
      // la mutación NO la hizo el propio elemento (que ya se re-renderiza solo,
      // marcando _internalFetchActive) → evita el doble render/parpadeo.
      const shouldRefresh = function () {
        const i = live();
        return i && !i._internalFetchActive ? i : null;
      };

      const ADD_TRIGGER_SEL =
        '.add-to-cart-button, button[name="add"], [name="add"], add-to-cart-component, [data-icd-upsell-add]';
      const markUserAdd = (e) => {
        try {
          if (!e || !e.isTrusted || !e.target || !e.target.closest) return;
          if (e.target.closest(ADD_TRIGGER_SEL)) { const i = live(); if (i) i._lastUserAddClick = Date.now(); }
        } catch (err) { /* swallow */ }
      };
      document.addEventListener('click', markUserAdd, true);
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') markUserAdd(e);
      }, true);

      // fetch: relanza un refresco tras mutaciones de carrito AJENAS (el tema u
      // otras apps). Las del propio elemento se saltan (shouldRefresh).
      const origFetch = window.fetch ? window.fetch.bind(window) : null;
      if (origFetch) {
        window.fetch = function (...args) {
          const result = origFetch(...args);
          try {
            const req = args[0];
            const url = typeof req === 'string' ? req : (req && req.url) || '';
            if (isCartRoute(url) && shouldRefresh()) {
              result
                .then((response) => {
                  if (response && response.ok) {
                    const i = shouldRefresh();
                    if (i) setTimeout(() => i._fetchCart({ autoOpen: isCartAdd(url) }), 80);
                  }
                })
                .catch(() => {});
            }
          } catch (e) { /* swallow */ }
          return result;
        };
      }

      // XMLHttpRequest
      const origOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url, ...rest) {
        this.__icdUrl = url;
        return origOpen.apply(this, [method, url, ...rest]);
      };
      const origSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.send = function (...args) {
        this.addEventListener('load', () => {
          try {
            const url = this.__icdUrl || '';
            if (isCartRoute(url) && this.status >= 200 && this.status < 300) {
              const i = shouldRefresh();
              if (i) setTimeout(() => i._fetchCart({ autoOpen: isCartAdd(url) }), 80);
            }
          } catch (e) { /* swallow */ }
        });
        return origSend.apply(this, args);
      };

      // Theme events
      [
        'cart:updated',
        'cart:refresh',
        'cart:change',
        'cart:item-added',
        'cart:item-removed',
        'ajaxCart:updated',
        'theme:cart:updated',
      ].forEach((ev) => {
        document.addEventListener(
          ev,
          () => { const i = shouldRefresh(); if (i) setTimeout(() => { const x = live(); if (x) x._fetchCart(); }, 120); },
          { passive: true }
        );
      });
    }

    /* ── Quick Buy popup (variant picker) ───────────────────────────── */

    async _openQuickBuy(handle) {
      if (!this.$qb) this.$qb = document.querySelector('[data-icd-qb]');
      if (!this.$qbBody) this.$qbBody = document.querySelector('[data-icd-qb-body]');
      if (!this.$qb || !this.$qbBody) return;
      try {
        const res = await fetch(`/products/${handle}.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error('Product fetch failed');
        const product = await res.json();
        this._renderQuickBuy(product);
        this.$qb.setAttribute('data-icd-qb-open', '');
        this.$qb.setAttribute('aria-hidden', 'false');
      } catch (e) {
        console.warn('[icd] _openQuickBuy failed', e);
      }
    }

    _closeQuickBuy() {
      if (!this.$qb) return;
      this.$qb.removeAttribute('data-icd-qb-open');
      this.$qb.setAttribute('aria-hidden', 'true');
      this._currentQbProduct = null;
      this._currentQbVariant = null;
      this._currentQbSelected = null;
      // Limpiar descuento bundle pendiente — si el cliente cerró sin añadir,
      // que la próxima apertura no arrastre estado del bundle anterior.
      this._pendingBundleDiscount = 0;
      this._pendingBundleRuleIdx = 0;
    }

    _renderQuickBuy(product) {
      this._currentQbProduct = product;
      this._currentQbSelected = {};

      const firstAvail =
        (product.variants || []).find((v) => v.available) || product.variants[0];
      if (firstAvail && product.options) {
        product.options.forEach((opt, idx) => {
          const optName = typeof opt === 'string' ? opt : opt.name;
          this._currentQbSelected[optName] = firstAvail['option' + (idx + 1)];
        });
      }
      this._currentQbVariant = firstAvail;

      const featuredImg =
        product.featured_image || (product.images && product.images[0]) || '';
      // Caja 80x120 (.icd-qb-img) -> base 120 (lado mayor)
      const img = sizedImage(featuredImg, 120);
      const imgSrcset = srcsetFor(featuredImg, 120);
      const initialPrice =
        firstAvail && firstAvail.price != null ? firstAvail.price : product.price;

      const optionsHTML = (product.options || [])
        .map((opt, idx) => this._qbOptionHTML(opt, idx, product))
        .join('');

      this.$qbBody.innerHTML = `
        <div class="icd-qb-product">
          <img class="icd-qb-img"
               src="${escapeHTML(img)}"
               srcset="${escapeHTML(imgSrcset)}"
               alt="${escapeHTML(product.title)}"
               width="80" height="120" loading="lazy" decoding="async">
          <div class="icd-qb-product-info">
            <p class="icd-qb-title">${escapeHTML(product.title)}</p>
            <div class="icd-qb-prices" data-icd-qb-prices>${this._qbPricesHTML(firstAvail || { price: initialPrice, compare_at_price: 0 })}</div>
          </div>
        </div>
        <form class="icd-qb-form" data-icd-qb-form>
          ${optionsHTML}
          <button type="submit" class="icd-qb-add" data-icd-qb-add>
            AÑADIR AL CARRITO
          </button>
        </form>
      `;

      this._updateQuickBuyVariant();
    }

    // Genera el HTML de precios para el popup Quick Buy:
    // - Si la variante tiene compare_at_price > price: tachado (#838383) + real (#E32C2B rojo)
    // - Si no: sólo precio real en #1C1C1C
    _qbPricesHTML(variant) {
      const price = Number(variant && variant.price) || 0;
      const compareAt = Number(variant && variant.compare_at_price) || 0;

      // Si el bundle activo tiene descuento visible, aplicamos el % al precio
      // del popup para que coincida con la tarjeta del recomendado. El tachado
      // (compare_at) se respeta siempre — NO se modifica el precio de Shopify.
      // Usamos _activeBundleDiscount (display, blindado por código), NO el
      // pending (que es solo para inyección de properties, hoy desactivada).
      const bundleDisc = Number(this._activeBundleDiscount) || 0;
      const finalPrice = bundleDisc > 0
        ? price - Math.round(price * bundleDisc / 100)
        : price;

      const hasCompareAt = compareAt > price;
      const isSale = hasCompareAt || bundleDisc > 0;
      return `${hasCompareAt ? `<span class="icd-qb-price-original">${this._formatMoney(compareAt)}</span>` : ''}<span class="icd-qb-price-final${isSale ? ' icd-qb-price-final--sale' : ''}">${this._formatMoney(finalPrice)}</span>`;
    }

    _qbOptionHTML(option, optionIdx, product) {
      const optName = typeof option === 'string' ? option : option.name;
      const values =
        typeof option === 'string'
          ? Array.from(
              new Set(
                product.variants
                  .map((v) => v['option' + (optionIdx + 1)])
                  .filter(Boolean)
              )
            )
          : option.values || [];

      const isColor = /color|colour/i.test(optName);
      const selected = this._currentQbSelected[optName];

      const swatchesHTML = values
        .map((value, valueIdx) => {
          const id = `icd-qb-opt-${optionIdx}-${valueIdx}`;
          const variantWithValue = product.variants.find(
            (v) => v['option' + (optionIdx + 1)] === value
          );
          const featuredImg =
            variantWithValue && variantWithValue.featured_image
              ? variantWithValue.featured_image.src ||
                variantWithValue.featured_image
              : null;
          const available = product.variants.some(
            (v) =>
              v['option' + (optionIdx + 1)] === value && v.available
          );
          const checked = value === selected ? 'checked' : '';
          const disabled = available ? '' : 'disabled';

          if (isColor && featuredImg) {
            return `
              <input type="radio" name="icd-qb-opt-${optionIdx}" value="${escapeHTML(value)}" id="${id}"
                     data-icd-qb-option data-option-idx="${optionIdx}" data-option-name="${escapeHTML(optName)}" ${checked} ${disabled}>
              <label for="${id}" class="icd-qb-media-swatch" title="${escapeHTML(value)}">
                <img src="${escapeHTML(sizedImage(featuredImg, 85))}" srcset="${escapeHTML(srcsetFor(featuredImg, 85))}" alt="${escapeHTML(value)}" loading="lazy">
              </label>
            `;
          }

          return `
            <input type="radio" name="icd-qb-opt-${optionIdx}" value="${escapeHTML(value)}" id="${id}"
                   data-icd-qb-option data-option-idx="${optionIdx}" data-option-name="${escapeHTML(optName)}" ${checked} ${disabled}>
            <label for="${id}" class="icd-qb-block-swatch">${escapeHTML(value)}</label>
          `;
        })
        .join('');

      const optionClass = isColor ? '' : ' icd-qb-option--size';
      const swatchesClass = isColor ? 'icd-qb-swatches--media' : 'icd-qb-swatches--block';

      return `
        <fieldset class="icd-qb-option${optionClass}">
          <legend>
            <span class="icd-qb-option-label">${escapeHTML(optName)}:</span>
            <span class="icd-qb-option-value" data-icd-qb-option-display="${optionIdx}">${escapeHTML(selected || '')}</span>
          </legend>
          <div class="icd-qb-swatches ${swatchesClass}">${swatchesHTML}</div>
        </fieldset>
      `;
    }

    _updateQuickBuyVariant() {
      const product = this._currentQbProduct;
      if (!product || !this.$qb) return;

      const checked = this.$qb.querySelectorAll(
        'input[data-icd-qb-option]:checked'
      );
      checked.forEach((input) => {
        const idx = parseInt(input.dataset.optionIdx, 10);
        const name = input.dataset.optionName;
        this._currentQbSelected[name] = input.value;
        const display = this.$qb.querySelector(
          `[data-icd-qb-option-display="${idx}"]`
        );
        if (display) display.textContent = input.value;
      });

      const variant = product.variants.find((v) =>
        product.options.every((opt, idx) => {
          const optName = typeof opt === 'string' ? opt : opt.name;
          return v['option' + (idx + 1)] === this._currentQbSelected[optName];
        })
      );

      this._currentQbVariant = variant;

      const pricesContainer = this.$qb.querySelector('[data-icd-qb-prices]');
      if (pricesContainer && variant) {
        pricesContainer.innerHTML = this._qbPricesHTML(variant);
      }

      const addBtn = this.$qb.querySelector('[data-icd-qb-add]');
      if (addBtn) {
        const available = !!(variant && variant.available);
        addBtn.disabled = !available;
        addBtn.textContent = available ? 'AÑADIR AL CARRITO' : 'AGOTADO';
      }

      // Imagen principal sigue al variant seleccionado (cambia con el color)
      const mainImg = this.$qb.querySelector('.icd-qb-img');
      if (mainImg) {
        let imgSrc = null;
        if (variant && variant.featured_image) {
          imgSrc =
            typeof variant.featured_image === 'string'
              ? variant.featured_image
              : variant.featured_image.src;
        }
        if (!imgSrc) {
          imgSrc =
            typeof product.featured_image === 'string'
              ? product.featured_image
              : (product.featured_image && product.featured_image.src) ||
                (product.images && product.images[0]);
        }
        if (imgSrc) {
          // Misma base 120 que al pintar el popup: si aquí se quedara otro
          // tamaño, la foto cambiaría de nitidez al elegir variante.
          const newSrc = sizedImage(imgSrc, 120);
          if (mainImg.getAttribute('src') !== newSrc) {
            mainImg.srcset = srcsetFor(imgSrc, 120);
            mainImg.src = newSrc;
          }
        }
      }
    }

    /**
     * Submit del Quick Buy popup. BULLETPROOF:
     *   - Lock `_qbSubmitting` previene doble-submit si el cliente
     *     hace doble-click rápido en "Añadir al carrito".
     *   - Estado visual con disabled + "AÑADIENDO..." durante la petición.
     *   - Restablece el botón en TODOS los caminos (éxito, fallo, throw).
     *   - Cierra popup solo si _addVariant tuvo éxito de verdad.
     */
    async _submitQuickBuy() {
      if (this._qbSubmitting) return; // ignora double-click
      const variant = this._currentQbVariant;
      if (!variant || !variant.available) return;

      this._qbSubmitting = true;
      const addBtn = this.$qb
        ? this.$qb.querySelector('[data-icd-qb-add]')
        : null;
      const originalText = addBtn ? addBtn.textContent : '';

      if (addBtn) {
        addBtn.disabled = true;
        addBtn.setAttribute('aria-busy', 'true');
        addBtn.textContent = 'AÑADIENDO...';
      }

      try {
        await this._addVariant(variant.id);
        this._closeQuickBuy();
      } catch (e) {
        console.warn('[icd] _submitQuickBuy failed', e);
      } finally {
        this._qbSubmitting = false;
        if (addBtn) {
          addBtn.disabled = false;
          addBtn.removeAttribute('aria-busy');
          addBtn.textContent = originalText || 'AÑADIR AL CARRITO';
        }
      }
    }
  }

  if (!customElements.get('icon-cart-drawer')) {
    customElements.define('icon-cart-drawer', IconCartDrawer);
  }
})();
